import React, { useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { supabase } from '../supabaseClient'
import jsPDF from 'jspdf'
import { Document, Packer, Paragraph, TextRun, AlignmentType } from 'docx'
import { saveAs } from 'file-saver'
import * as XLSX from 'xlsx'
import './Servicos.css'

function fmtDateISO(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function nomeTipoNormalizado(nomeTipo) {
  return (nomeTipo || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

/**
 * Tipos de serviço cuja cobrança é Qtd. de módulos × valor da tabela de preço.
 * Inclui Desinstalação e Desinstalação/Reinstalação.
 */
function tipoPorQuantidadeModulos(nomeTipo) {
  const nome = nomeTipoNormalizado(nomeTipo)
  return nome === 'desinstalacao' || nome === 'desinstalacao/reinstalacao'
}

function isInstalacaoNome(nomeTipo) {
  return nomeTipoNormalizado(nomeTipo) === 'instalacao'
}

function mesmoNomeTipo(a, b) {
  const na = nomeTipoNormalizado(a)
  const nb = nomeTipoNormalizado(b)
  return !!na && na === nb
}

/** Identifica o tipo de serviço "Obra Cívil" (cobrança pelo Valor da Obra, ignorando a tabela de preço). */
function isObraCivilNome(nomeTipo) {
  return nomeTipoNormalizado(nomeTipo) === 'obra civil'
}

function clienteEhDoTipo(c, os) {
  if (!c || !os) return false
  if (os.id_tipo_servico && c.tipo_servico_id && String(c.tipo_servico_id) === String(os.id_tipo_servico)) return true
  if (os.tipo_servico?.nome && c.tipo_servico?.nome && mesmoNomeTipo(c.tipo_servico.nome, os.tipo_servico.nome)) return true
  return false
}

function escolherMaisProximoDaData(arr, dataRef) {
  if (!arr || arr.length === 0) return null
  const t = dataRef ? new Date(dataRef).getTime() : 0
  return arr.reduce((best, c) => {
    const d = c.data_instalacao ? new Date(c.data_instalacao).getTime() : 0
    const diff = Math.abs(d - t)
    if (!best) return c
    const bestDiff = best.data_instalacao
      ? Math.abs(new Date(best.data_instalacao).getTime() - t)
      : Infinity
    return diff < bestDiff ? c : best
  }, null)
}

/** Baixa em `clientes` do mesmo tipo da OS. */
function escolherClienteDoTipo(arr, os) {
  if (!arr || arr.length === 0) return null
  return escolherMaisProximoDaData(arr.filter((c) => clienteEhDoTipo(c, os)), os?.data_execucao)
}

function temCamposInstalacao(c) {
  return c != null && (c.tipo_padrao_id != null || c.quantidade_modulos != null)
}

/**
 * Instalação: prefere a baixa do mesmo tipo; se o tipo foi sobrescrito pela Obra Civil,
 * reaproveita tipo padrão / qtd. de módulos da linha que ainda tem esses campos.
 */
function escolherClienteInstalacao(arr, os) {
  const doTipo = escolherClienteDoTipo(arr, os)
  if (doTipo) return { row: doTipo, mesmoTipo: true }
  const semObraCivil = (arr || []).filter((c) => temCamposInstalacao(c) && !isObraCivilNome(c.tipo_servico?.nome))
  const comCampos = semObraCivil.length > 0 ? semObraCivil : (arr || []).filter(temCamposInstalacao)
  const row = escolherMaisProximoDaData(comCampos, os?.data_execucao)
  return row ? { row, mesmoTipo: false } : null
}

/** Retorna { inicio, fim } da semana atual (segunda a domingo) em YYYY-MM-DD */
function getSemanaAtual() {
  const d = new Date()
  const day = d.getDay()
  const diasParaSegunda = day === 0 ? 6 : day - 1
  const segunda = new Date(d.getFullYear(), d.getMonth(), d.getDate() - diasParaSegunda)
  const domingo = new Date(segunda)
  domingo.setDate(segunda.getDate() + 6)
  return { inicio: fmtDateISO(segunda), fim: fmtDateISO(domingo) }
}

const COOKIE_SERVICOS_DATA_INICIO = 'servicos_filtro_data_inicio'
const COOKIE_SERVICOS_DATA_FIM = 'servicos_filtro_data_fim'
const COOKIE_MAX_AGE_DAYS = 365

function getCookie(name) {
  if (typeof document === 'undefined') return ''
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
  return match ? decodeURIComponent(match[2]) : ''
}

function setCookie(name, value, maxAgeDays) {
  if (typeof document === 'undefined') return
  const maxAge = maxAgeDays * 24 * 60 * 60
  document.cookie = name + '=' + encodeURIComponent(value) + '; path=/; max-age=' + maxAge + '; SameSite=Lax'
}

function isValidDateYYYYMMDD(s) {
  return /^\d{4}-\d{2}-\d{2}$/.test(s)
}

/** Instalação, OS ou vistoria com estes status não aparecem na tela Serviços. */
function statusEhCancelado(s) {
  if (s == null) return false
  const t = String(s).trim().toLowerCase()
  return t === 'cancelado' || t === 'cancelada'
}

/** Registro em clientes ou vistorias usado para fechar a medição. */
function alvoFecharMedicao(row) {
  if (!row) return null
  if (row.origem === 'vistoria' && row.id_vistoria != null) {
    return { tabela: 'vistorias', id: row.id_vistoria }
  }
  if (row.origem === 'ordem_servico' && row.cliente_instalacao_id) {
    return { tabela: 'clientes', id: row.cliente_instalacao_id }
  }
  if (row.origem !== 'vistoria' && row.origem !== 'ordem_servico' && row.id) {
    return { tabela: 'clientes', id: row.id }
  }
  return null
}

function podeFecharMedicao(row) {
  return !row?.medicao_fechada && alvoFecharMedicao(row) != null
}

function dataServicoISO(row) {
  const d = row?.data_instalacao
  if (!d) return null
  const s = typeof d === 'string' ? d : (d instanceof Date ? d.toISOString() : String(d))
  if (s.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  const dt = new Date(d)
  if (isNaN(dt.getTime())) return null
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`
}

function numeroOuNull(v) {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isNaN(n) ? null : n
}

/** Extrai URLs de campo vistoria (string única ou JSON array). */
function urlsFromCampoImagemVistoria(val) {
  const coletar = (input) => {
    if (input == null || input === '') return []
    if (Array.isArray(input)) {
      return input.flatMap((x) => coletar(x))
    }
    if (typeof input === 'object') {
      const out = []
      for (const v of Object.values(input)) out.push(...coletar(v))
      return out
    }
    const s = String(input).trim()
    if (!s) return []
    if ((s.startsWith('[') && s.endsWith(']')) || (s.startsWith('{') && s.endsWith('}'))) {
      try {
        const parsed = JSON.parse(s)
        return coletar(parsed)
      } catch (_) {
        /* ignore */
      }
    }
    return [s]
  }

  return [...new Set(coletar(val).map(String).filter(Boolean))]
}

function normalizarCaminhoStorage(raw, bucketPadrao = '') {
  if (raw == null) return null
  const s = String(raw).trim()
  if (!s) return null
  if (/^https?:\/\//i.test(s)) return s
  const semQuery = s.split('?')[0].split('#')[0]
  const marker = '/storage/v1/object/public/'
  const idxMarker = semQuery.indexOf(marker)
  if (idxMarker >= 0) {
    const resto = semQuery.slice(idxMarker + marker.length)
    const partes = resto.split('/').filter(Boolean)
    if (partes.length >= 2) {
      const bucket = partes[0]
      const caminho = partes.slice(1).join('/')
      return { bucket, caminho }
    }
  }
  const limpo = semQuery.replace(/^\/+/, '')
  const partes = limpo.split('/').filter(Boolean)
  if (partes.length >= 2) return { bucket: partes[0], caminho: partes.slice(1).join('/') }
  if (bucketPadrao) return { bucket: bucketPadrao, caminho: limpo }
  return null
}

function resolverImagemPublica(raw, bucketPadrao = '') {
  const info = normalizarCaminhoStorage(raw, bucketPadrao)
  if (!info) return null
  if (typeof info === 'string') return info
  const { data } = supabase.storage.from(info.bucket).getPublicUrl(info.caminho)
  return data?.publicUrl || null
}

function labelFotoFromKey(key) {
  return String(key || '')
    .replace(/^caminho_img_/i, '')
    .replace(/^foto_/i, '')
    .replace(/^img_/i, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase()) || 'Foto'
}

function extrairFotosRegistroGenerico(registro, bucketPadrao = '') {
  if (!registro || typeof registro !== 'object') return []
  const itens = []
  for (const [key, value] of Object.entries(registro)) {
    const nome = String(key || '').toLowerCase()
    const pareceCampoFoto = /foto|img|imagem|caminho/.test(nome)
    if (!pareceCampoFoto) continue
    const urls = urlsFromCampoImagemVistoria(value)
    for (const rawUrl of urls) {
      const publicUrl = resolverImagemPublica(rawUrl, bucketPadrao)
      if (!publicUrl) continue
      if (itens.some((x) => x.url === publicUrl)) continue
      itens.push({ label: labelFotoFromKey(key), url: publicUrl })
    }
  }
  return itens
}

const LABELS_FOTOS_BAIXA = {
  foto_planta_monitoramento: 'Foto da Planta de Monitoramento',
  foto_fachada: 'Foto da Fachada',
  foto_padrao_completo: 'Foto do Padrão Completo',
  foto_etiqueta_inversor: 'Foto da Etiqueta do Inversor',
  foto_datalogger: 'Foto do Datalogger',
  foto_instalacao_inversor: 'Foto da Instalação do Inversor',
  foto_qpca: 'Foto do QPCA',
  foto_disjuntor_padrao: 'Foto do Disjuntor do Padrão',
  foto_aterramento_padrao: 'Foto do Aterramento do Padrão',
  foto_conexoes_padrao: 'Foto das Conexões do Padrão',
  foto_disjuntor_qpca: 'Foto do Disjuntor do QPCA',
  foto_aterramento_qpca: 'Foto do Aterramento do QPCA',
  foto_medicoes_strings: 'Foto das Medições das Strings',
  foto_antes_modulos: 'Foto Antes de Instalar os Módulos',
  foto_apos_modulos: 'Foto Após Instalar os Módulos',
  foto_strings: 'Foto das Strings',
  foto_antes_manutencao_correcao: 'Foto antes da Manutenção/Correção',
  foto_apos_manutencao_correcao: 'Foto após a Manutenção/Correção'
}

function extrairFotosFormularioBaixaResolve(registro) {
  if (!registro) return []
  let fotos = registro.fotos
  if (typeof fotos === 'string') {
    try {
      fotos = JSON.parse(fotos)
    } catch (_) {
      fotos = null
    }
  }
  if (!fotos || typeof fotos !== 'object') return []
  const itens = []
  for (const [campo, urls] of Object.entries(fotos)) {
    const label = LABELS_FOTOS_BAIXA[campo] || labelFotoFromKey(campo)
    for (const rawUrl of urlsFromCampoImagemVistoria(urls)) {
      const publicUrl = resolverImagemPublica(rawUrl, 'fotos-finalizacao')
      if (!publicUrl) continue
      if (itens.some((x) => x.url === publicUrl)) continue
      itens.push({ label, url: publicUrl })
    }
  }
  return itens
}

const CAMPOS_FOTOS_VISTORIA = [
  ['caminho_img_padrao', 'Padrão'],
  ['caminho_img_inversor', 'Inversor'],
  ['caminho_img_fachada', 'Fachada'],
  ['caminho_img_mapa', 'Mapa'],
  ['caminho_img_telhado', 'Telhado'],
  ['caminho_img_quadro', 'Quadro'],
  ['caminho_img_estrutura', 'Estrutura'],
  ['caminho_img_croqui', 'Croqui']
]

function coletarFotosObjetoVistoria(v) {
  if (!v) return []
  const out = []
  for (const [key, label] of CAMPOS_FOTOS_VISTORIA) {
    for (const url of urlsFromCampoImagemVistoria(v[key])) {
      const publicUrl = resolverImagemPublica(url, 'vistorias-fotos')
      if (publicUrl) out.push({ label, url: publicUrl })
    }
  }
  return out
}

function coletarFotosVistoriaMesmoMetodo(v) {
  if (!v) return []
  const itens = []
  const addUnica = (campo, label) => {
    if (!v[campo]) return
    const url = resolverImagemPublica(v[campo], 'vistorias-fotos')
    if (url && !itens.some((x) => x.url === url)) itens.push({ label, url })
  }
  const addMultiplas = (campo, labelBase) => {
    if (!v[campo]) return
    let urls = []
    try {
      const parsed = typeof v[campo] === 'string' ? JSON.parse(v[campo]) : v[campo]
      urls = Array.isArray(parsed) ? parsed : [v[campo]]
    } catch (_) {
      urls = [v[campo]]
    }
    urls.forEach((u, idx) => {
      const url = resolverImagemPublica(u, 'vistorias-fotos')
      if (!url || itens.some((x) => x.url === url)) return
      itens.push({ label: `${labelBase}${urls.length > 1 ? ` ${idx + 1}/${urls.length}` : ''}`, url })
    })
  }

  addUnica('caminho_img_padrao', 'Padrão')
  addUnica('caminho_img_inversor', 'Inversor')
  addUnica('caminho_img_fachada', 'Fachada')
  addUnica('caminho_img_mapa', 'Mapa')
  addMultiplas('caminho_img_telhado', 'Telhado')
  addUnica('caminho_img_quadro', 'Quadro')
  addMultiplas('caminho_img_estrutura', 'Estrutura')
  addUnica('caminho_img_croqui', 'Croqui')
  return itens
}

function formatarDataBrInfo(dataString) {
  if (!dataString) return '–'
  try {
    const s = String(dataString).split('T')[0].trim()
    const [ano, mes, dia] = s.split('-')
    if (ano && mes && dia) return `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}/${ano}`
  } catch (_) {}
  return '–'
}

function formatarNumeroInfo(n) {
  if (n == null || n === '') return '–'
  const x = Number(n)
  if (Number.isNaN(x)) return String(n)
  return x.toLocaleString('pt-BR', { maximumFractionDigits: 2 })
}

function pushInfoLinha(arr, label, value, opts = {}) {
  const { skipIfEmpty = true } = opts
  if (skipIfEmpty && (value == null || value === '')) return
  arr.push({ label, value: value == null || value === '' ? '–' : String(value) })
}

/** Linhas de texto a partir do registro em `vistorias` (sem campos de imagem). */
function montarLinhasInfoVistoria(v, deslocamentoCalc = null) {
  if (!v) return []
  const L = []
  const tipoNome = v.tipo_padrao?.nome
  pushInfoLinha(L, 'Data da vistoria', formatarDataBrInfo(v.data_vistoria), { skipIfEmpty: false })
  pushInfoLinha(L, 'Status', v.status)
  if (tipoNome) pushInfoLinha(L, 'Tipo de padrão', tipoNome)
  pushInfoLinha(L, 'Ligação padrão', v.ligacao_padrao)
  pushInfoLinha(L, 'Local do inversor', v.local_inversor)
  pushInfoLinha(L, 'Poste concessionária', v.posicao_poste_concessionaria)
  pushInfoLinha(L, 'Conexão inversor', v.conexao_inversor)
  pushInfoLinha(L, 'Local aterramento', v.local_aterramento_sistema)
  pushInfoLinha(L, 'Metragem C.A. inversor (m)', formatarNumeroInfo(v.metragem_ca_inversor))
  pushInfoLinha(L, 'Metragem C.C. (m)', formatarNumeroInfo(v.metragem_cc))
  pushInfoLinha(L, 'Metragem C.A. padrão — fases (m)', formatarNumeroInfo(v.metragem_ca_padrao_fases))
  pushInfoLinha(L, 'Metragem C.A. padrão — neutros (m)', formatarNumeroInfo(v.metragem_ca_padrao_neutros))
  pushInfoLinha(L, 'Qtd. conduletes inversor', v.qtd_conduletes_inversor != null ? String(v.qtd_conduletes_inversor) : null)
  pushInfoLinha(L, 'Qtd. eletroduto inversor', v.qtd_eletroduto_inversor != null ? String(v.qtd_eletroduto_inversor) : null)
  pushInfoLinha(L, 'Qtd. eletroduto padrão', v.qtd_eletroduto_padrao != null ? String(v.qtd_eletroduto_padrao) : null)
  if (v.qtd_modulos_telhado != null) pushInfoLinha(L, 'Qtd. módulos telhado', String(v.qtd_modulos_telhado))
  if (v.percurso_aterramento != null) pushInfoLinha(L, 'Percurso aterramento (m)', formatarNumeroInfo(v.percurso_aterramento))
  if (v.km_rodado != null) pushInfoLinha(L, 'Km rodado', formatarNumeroInfo(v.km_rodado))
  if (v.tipo_veiculo) pushInfoLinha(L, 'Tipo de veículo', v.tipo_veiculo.charAt(0).toUpperCase() + v.tipo_veiculo.slice(1))
  if (deslocamentoCalc != null) {
    pushInfoLinha(L, 'Deslocamento', formatarMoedaBrInfo(deslocamentoCalc))
  } else if (v.deslocamento != null) {
    pushInfoLinha(L, 'Deslocamento', formatarNumeroInfo(v.deslocamento))
  }
  if (v.observacoes && String(v.observacoes).trim()) {
    L.push({ label: 'Observações', value: String(v.observacoes).trim() })
  }
  return L
}

function formatarMoedaBrInfo(v) {
  if (v == null || v === '' || Number.isNaN(Number(v))) return '–'
  return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function montarLinhasInfoClienteInstalacao(c) {
  if (!c) return []
  const L = []
  pushInfoLinha(L, 'Data instalação', formatarDataBrInfo(c.data_instalacao))
  pushInfoLinha(L, 'Tipo de serviço', c.tipo_servico?.nome)
  pushInfoLinha(L, 'Tipo padrão', c.tipo_padrao?.nome)
  pushInfoLinha(L, 'Qtd. módulos', c.quantidade_modulos != null ? String(c.quantidade_modulos) : null)
  pushInfoLinha(L, 'Valor obra', formatarMoedaBrInfo(c.valor_obra))
  // Valor material só é exibido quando o tipo de serviço é "Instalação".
  const tipoNomeInfo = (c.tipo_servico?.nome || '').trim().toLowerCase()
  if (tipoNomeInfo === 'instalação' || tipoNomeInfo === 'instalacao') {
    pushInfoLinha(L, 'Valor material', formatarMoedaBrInfo(c.valor_material))
  }
  pushInfoLinha(L, 'Deslocamento', formatarMoedaBrInfo(c.deslocamento))
  pushInfoLinha(L, 'Nota material', c.nota_material)
  return L
}

function montarLinhasInfoCadastroCliente(c) {
  if (!c) return []
  const L = []
  pushInfoLinha(L, 'Nome', c.nome_completo)
  pushInfoLinha(L, 'Telefone', c.telefone)
  pushInfoLinha(L, 'CPF', c.cpf)
  pushInfoLinha(L, 'E-mail', c.email)
  const end = [c.logradouro, c.numero, c.complemento].filter(Boolean).join(', ')
  pushInfoLinha(L, 'Endereço', end || null)
  pushInfoLinha(L, 'Bairro', c.bairro)
  pushInfoLinha(L, 'Cidade / UF', c.cidade && c.estado ? `${c.cidade} - ${c.estado}` : (c.cidade || c.estado || null))
  pushInfoLinha(L, 'CEP', c.cep)
  if (c.observacoes && String(c.observacoes).trim()) {
    L.push({ label: 'Observações (cadastro)', value: String(c.observacoes).trim() })
  }
  return L
}

/**
 * Tela de Serviços
 * Carrega dados da tabela clientes com:
 * nome_cliente, data_instalacao, tipo_servico (nome), tipo_padrao (nome),
 * quantidade_modulos, nota_material, valor_obra, valor_material
 */
const INIT_CONTRATADORA = {
  nome_empresa: '',
  razao_social: '',
  cnpj_cpf: '',
  endereco: '',
  numero: '',
  cep: '',
  bairro: '',
  cidade: '',
  estado: '',
  telefone: '',
  email: ''
}

const Servicos = ({ onBack, userRole, userEmpresa, userId }) => {
  const { inicio: defaultInicio, fim: defaultFim } = getSemanaAtual()
  const [filtroDataInicio, setFiltroDataInicio] = useState(() => {
    const saved = getCookie(COOKIE_SERVICOS_DATA_INICIO)
    return typeof document !== 'undefined' && isValidDateYYYYMMDD(saved) ? saved : defaultInicio
  })
  const [filtroDataFim, setFiltroDataFim] = useState(() => {
    const saved = getCookie(COOKIE_SERVICOS_DATA_FIM)
    return typeof document !== 'undefined' && isValidDateYYYYMMDD(saved) ? saved : defaultFim
  })
  const [filtroTipoServico, setFiltroTipoServico] = useState('')
  const [tiposServicoFiltroList, setTiposServicoFiltroList] = useState([])
  const [filtroEmpresa, setFiltroEmpresa] = useState('')
  const [listaEmpresas, setListaEmpresas] = useState([])
  const [servicos, setServicos] = useState([])
  const [totalFornecidoPorOrdem, setTotalFornecidoPorOrdem] = useState({})
  // Fallback por cliente: usado nas linhas da grade que vêm da tabela `clientes` (sem id_ordem_servico),
  // já que a lista de material é vinculada à OS (cuja data pode diferir da data da instalação).
  const [totalMaterialPorCliente, setTotalMaterialPorCliente] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showModalContratadora, setShowModalContratadora] = useState(false)
  const [tipoCadastroContratadora, setTipoCadastroContratadora] = useState('cnpj')
  const [formContratadora, setFormContratadora] = useState(INIT_CONTRATADORA)
  const [savingContratadora, setSavingContratadora] = useState(false)
  const [msgContratadora, setMsgContratadora] = useState({ type: '', text: '' })
  const [searchCepContratadora, setSearchCepContratadora] = useState(false)
  const [showConfirmacaoCadastro, setShowConfirmacaoCadastro] = useState(false)
  const [empresasContratadoras, setEmpresasContratadoras] = useState([])
  const [atualizandoContratante, setAtualizandoContratante] = useState(null)
  const [kitSolarPorClienteCadastro, setKitSolarPorClienteCadastro] = useState({})
  const [showModalPadraoCobranca, setShowModalPadraoCobranca] = useState(false)
  const [formPadraoCobranca, setFormPadraoCobranca] = useState({ tabela_preco_id: '', id_padrao: '', valor: '' })
  const [savingPadraoCobranca, setSavingPadraoCobranca] = useState(false)
  const [msgPadraoCobranca, setMsgPadraoCobranca] = useState({ type: '', text: '' })
  const [showConfirmacaoPadraoCobranca, setShowConfirmacaoPadraoCobranca] = useState(false)
  const [msgConfirmacaoPadrao, setMsgConfirmacaoPadrao] = useState('Tipo padrão cobrança cadastrado com sucesso.')
  const [listaPadroesCobrancaTabela, setListaPadroesCobrancaTabela] = useState([])
  const [atualizandoValorPadrao, setAtualizandoValorPadrao] = useState(null)
  const [valorEditPadrao, setValorEditPadrao] = useState({})
  const [idPadraoEditPadrao, setIdPadraoEditPadrao] = useState({})
  const [excluindoPadrao, setExcluindoPadrao] = useState(null)
  const [tabelaPrecosList, setTabelaPrecosList] = useState([])
  const [tipoPadraoList, setTipoPadraoList] = useState([])
  const [showModalTabelaPrecos, setShowModalTabelaPrecos] = useState(false)
  const [formTabelaPrecos, setFormTabelaPrecos] = useState({ nome_tabela: '', tipo_cobranca: 'Modulo', valor_unitario: '', tabela_preco_id_edicao: '' })
  const [tiposServicoList, setTiposServicoList] = useState([])
  const [precosServicosPorTipo, setPrecosServicosPorTipo] = useState({})
  const [faixasKwp, setFaixasKwp] = useState([])
  const [currentFaixaKwp, setCurrentFaixaKwp] = useState({ kwp_min: '', kwp_max: '', valor: '' })
  const [savingTabelaPrecos, setSavingTabelaPrecos] = useState(false)
  const [msgTabelaPrecos, setMsgTabelaPrecos] = useState({ type: '', text: '' })
  const [showConfirmacaoTabelaPrecos, setShowConfirmacaoTabelaPrecos] = useState(false)
  const [showCadastrosMenu, setShowCadastrosMenu] = useState(false)
  const [tiposCobrancaList, setTiposCobrancaList] = useState([])
  const [selectedTabelaPrecoId, setSelectedTabelaPrecoId] = useState('')
  const [faixasKwpSelecionada, setFaixasKwpSelecionada] = useState([])
  const [valorPadraoPorTipoPadrao, setValorPadraoPorTipoPadrao] = useState({})
  const [valorServicoPorTipoTabela, setValorServicoPorTipoTabela] = useState({})
  const [showModalValidacao, setShowModalValidacao] = useState(false)
  const [msgValidacao, setMsgValidacao] = useState('')
  const [showModalGerarRecibo, setShowModalGerarRecibo] = useState(false)
  const [showModalDetalheServico, setShowModalDetalheServico] = useState(false)
  const [detalheServicoTipo, setDetalheServicoTipo] = useState(null) // 'fotos' | 'lista'
  const [detalheServicoRow, setDetalheServicoRow] = useState(null)
  const [detalheServicoLoading, setDetalheServicoLoading] = useState(false)
  const [detalheFotosItems, setDetalheFotosItems] = useState([])
  const [detalheFotosExtras, setDetalheFotosExtras] = useState({ linkLocalizacao: null })
  /** { titulo: string, linhas: { label, value }[] }[] */
  const [detalheFotosInfoSecoes, setDetalheFotosInfoSecoes] = useState([])
  const [detalheListasMaterial, setDetalheListasMaterial] = useState([])
  const [ordensComListaMaterial, setOrdensComListaMaterial] = useState(() => new Set())
  const [clientesComListaMaterial, setClientesComListaMaterial] = useState(() => new Set())
  const cadastrosWrapRef = useRef(null)
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches)
  const [showModalFecharMedicao, setShowModalFecharMedicao] = useState(false)
  const [rowFecharMedicao, setRowFecharMedicao] = useState(null)
  const [fechandoMedicao, setFechandoMedicao] = useState(false)
  const [msgSucessoMedicao, setMsgSucessoMedicao] = useState('')

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)')
    const handler = (e) => setIsMobile(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  useEffect(() => {
    if (isValidDateYYYYMMDD(filtroDataInicio)) setCookie(COOKIE_SERVICOS_DATA_INICIO, filtroDataInicio, COOKIE_MAX_AGE_DAYS)
    if (isValidDateYYYYMMDD(filtroDataFim)) setCookie(COOKIE_SERVICOS_DATA_FIM, filtroDataFim, COOKIE_MAX_AGE_DAYS)
  }, [filtroDataInicio, filtroDataFim])

  const showErroValidacao = (texto) => {
    setMsgValidacao(texto)
    setShowModalValidacao(true)
  }

  const abrirConfirmacaoFecharMedicao = (row = null) => {
    if (userRole !== 'administrador') return
    if (row) {
      if (!podeFecharMedicao(row)) {
        showErroValidacao(row.medicao_fechada
          ? 'A medição deste serviço já está fechada.'
          : 'Este serviço ainda não tem baixa cadastrada. Não é possível fechar a medição.')
        return
      }
      setRowFecharMedicao(row)
      setShowModalFecharMedicao(true)
      return
    }
    const pendentes = servicos.filter(podeFecharMedicao)
    if (pendentes.length === 0) {
      showErroValidacao('Não há serviços com baixa para fechar a medição na lista atual.')
      return
    }
    setRowFecharMedicao(null)
    setShowModalFecharMedicao(true)
  }

  const confirmarFecharMedicao = async () => {
    if (userRole !== 'administrador') return
    const rows = rowFecharMedicao ? [rowFecharMedicao] : servicos.filter(podeFecharMedicao)
    const alvos = rows.map(alvoFecharMedicao).filter(Boolean)
    if (alvos.length === 0) {
      setShowModalFecharMedicao(false)
      setRowFecharMedicao(null)
      showErroValidacao('Não há serviços com baixa para fechar a medição.')
      return
    }
    setFechandoMedicao(true)
    try {
      const idsClientes = [...new Set(alvos.filter((a) => a.tabela === 'clientes').map((a) => a.id))]
      const idsVistorias = [...new Set(alvos.filter((a) => a.tabela === 'vistorias').map((a) => a.id))]
      if (idsClientes.length > 0) {
        const { error: errClientes } = await supabase
          .from('clientes')
          .update({ medicao_fechada: true })
          .in('id', idsClientes)
        if (errClientes) throw errClientes
      }
      if (idsVistorias.length > 0) {
        const { error: errVistorias } = await supabase
          .from('vistorias')
          .update({ medicao_fechada: true })
          .in('id', idsVistorias)
        if (errVistorias) throw errVistorias
      }

      const loteId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`
      const replicas = []
      const jaIncluido = new Set()
      for (const row of rows) {
        const alvo = alvoFecharMedicao(row)
        if (!alvo) continue
        const chave = `${alvo.tabela}:${alvo.id}`
        if (jaIncluido.has(chave)) continue
        jaIncluido.add(chave)
        replicas.push(montarReplicaMedicao(row, loteId, alvo))
      }
      if (replicas.length > 0) {
        const { error: errReplica } = await supabase
          .from('medicao_fechada')
          .insert(replicas)
        if (errReplica) throw errReplica
      }

      setServicos((prev) => prev.map((r) => {
        const alvo = alvoFecharMedicao(r)
        if (!alvo) return r
        const fechou = alvos.some((a) => a.tabela === alvo.tabela && String(a.id) === String(alvo.id))
        return fechou ? { ...r, medicao_fechada: true } : r
      }))
      setShowModalFecharMedicao(false)
      setRowFecharMedicao(null)
      setMsgSucessoMedicao(
        rows.length === 1
          ? 'Medição fechada. A baixa não poderá mais ser editada.'
          : `Medição fechada em ${rows.length} serviço(s). As baixas não poderão mais ser editadas.`
      )
    } catch (err) {
      console.error('Erro ao fechar medição:', err)
      showErroValidacao(err.message || 'Erro ao fechar a medição.')
    } finally {
      setFechandoMedicao(false)
    }
  }

  useEffect(() => {
    if (!showCadastrosMenu) return
    const handleClickOutside = (e) => {
      if (cadastrosWrapRef.current && !cadastrosWrapRef.current.contains(e.target)) {
        setShowCadastrosMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showCadastrosMenu])

  const loadServicos = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const inicio = `${filtroDataInicio}T00:00:00`
      const fim = `${filtroDataFim}T23:59:59`
      const inicioDate = filtroDataInicio
      const fimDate = filtroDataFim

      const { data: tiposData, error: tiposError } = await supabase.from('tipo_servico').select('id, nome, valor_servico').order('nome')
      if (tiposError) {
        console.error('Erro ao carregar tipos de serviço para o filtro:', tiposError)
      }
      let tiposList = tiposData || []
      const nomeTipoIgualVistoria = (t) => (t?.nome || '').trim().toLowerCase() === 'vistoria'
      const vistoriaTipo = tiposList.find(nomeTipoIgualVistoria)
      const vistoriaTipoId = vistoriaTipo?.id ?? null
      const valorServicoVistoria = vistoriaTipo != null ? Number(vistoriaTipo.valor_servico) || 0 : 0

      if (userRole === 'administrador') {
        const { data: empresasData } = await supabase.from('empresa').select('id, empresa, razao_social, cnpj, endereco').order('empresa')
        setListaEmpresas(empresasData || [])
      }

      const soVistoria = filtroTipoServico !== '' && (String(filtroTipoServico) === 'vistoria' || String(filtroTipoServico) === String(vistoriaTipoId))
      const soOutrosTipos = filtroTipoServico !== '' && !soVistoria
      // Só retornar registros da empresa do usuário logado; administrador é exceção (pode ver todos ou filtrar)
      const empresaFiltroId = userRole === 'administrador'
        ? (filtroEmpresa !== '' ? filtroEmpresa : null)
        : (userEmpresa?.id ?? null)
      const isNonAdminSemEmpresa = userRole !== 'administrador' && !userEmpresa?.id

      /** Quando há empresa no contexto, filtra pela empresa do cadastro do cliente (clientes_cadastro.empresa_id). */
      let idsClienteCadastroPorEmpresa = null
      if (empresaFiltroId) {
        const { data: cadastroIdsRows, error: errCadastroIds } = await supabase
          .from('clientes_cadastro')
          .select('id')
          .eq('empresa_id', empresaFiltroId)
        if (errCadastroIds) throw errCadastroIds
        idsClienteCadastroPorEmpresa = (cadastroIdsRows || []).map((r) => r.id).filter(Boolean)
      }
      const semCadastroNestaEmpresa =
        empresaFiltroId && (!idsClienteCadastroPorEmpresa || idsClienteCadastroPorEmpresa.length === 0)

      let servicosData = []
      if (!isNonAdminSemEmpresa && !soVistoria && !semCadastroNestaEmpresa) {
        let query = supabase
          .from('clientes')
          .select(`
            id,
            nome_cliente,
            data_instalacao,
            tipo_servico_id,
            tipo_padrao_id,
            quantidade_modulos,
            nota_material,
            valor_obra,
            valor_material,
            deslocamento,
            empresa_id,
            id_contratante,
            id_cliente_cadastro,
            id_status,
            medicao_fechada,
            status_info:status_clientes(status),
            tipo_servico:tipo_servico(nome),
            tipo_padrao:tipo_padrao(nome)
          `)
          .gte('data_instalacao', inicio)
          .lte('data_instalacao', fim)
          .order('data_instalacao', { ascending: false })
        if (empresaFiltroId) query = query.in('id_cliente_cadastro', idsClienteCadastroPorEmpresa)
        if (soOutrosTipos) query = query.eq('tipo_servico_id', filtroTipoServico)
        const { data, error: fetchError } = await query
        if (fetchError) throw fetchError
        servicosData = (data || []).filter((c) => !statusEhCancelado(c.status_info?.status))
      }

      let ordensServicosRows = []
      if (!isNonAdminSemEmpresa && !semCadastroNestaEmpresa) {
        let queryOS = supabase
          .from('ordens_servicos')
          .select(`
            id,
            id_cliente,
            id_tipo_servico,
            id_empresa,
            data_execucao,
            status,
            cliente_cadastro:clientes_cadastro!id_cliente(nome_completo),
            tipo_servico(id, nome)
          `)
          .gte('data_execucao', inicioDate)
          .lte('data_execucao', fimDate)
          .order('data_execucao', { ascending: false })
        if (empresaFiltroId) queryOS = queryOS.in('id_cliente', idsClienteCadastroPorEmpresa)
        if (soOutrosTipos) queryOS = queryOS.eq('id_tipo_servico', filtroTipoServico)
        const { data: osData, error: osError } = await queryOS
        const osDataFiltrada = (osData || []).filter(
          (os) =>
            !statusEhCancelado(os.status) &&
            (os.tipo_servico?.nome || '').trim().toLowerCase() !== 'vistoria'
        )
        if (!osError && osDataFiltrada.length > 0) {
          const idsClienteOS = [...new Set(osDataFiltrada.map((os) => os.id_cliente).filter(Boolean))]
          let clientesPorClienteCadastro = {}
          if (idsClienteOS.length > 0) {
            const { data: clientesInstalacao } = await supabase
              .from('clientes')
              .select(`
                id,
                id_cliente_cadastro,
                data_instalacao,
                tipo_servico_id,
                tipo_padrao_id,
                quantidade_modulos,
                valor_obra,
                valor_material,
                deslocamento,
                id_contratante,
                medicao_fechada,
                tipo_servico:tipo_servico(nome),
                tipo_padrao:tipo_padrao(nome)
              `)
              .in('id_cliente_cadastro', idsClienteOS)
            if (clientesInstalacao && clientesInstalacao.length > 0) {
              clientesInstalacao.forEach((c) => {
                const key = c.id_cliente_cadastro
                if (!clientesPorClienteCadastro[key]) clientesPorClienteCadastro[key] = []
                clientesPorClienteCadastro[key].push(c)
              })
              clientesPorClienteCadastro = Object.fromEntries(
                Object.entries(clientesPorClienteCadastro).map(([k, arr]) => [
                  k,
                  arr.sort((a, b) => new Date(b.data_instalacao || 0) - new Date(a.data_instalacao || 0))
                ])
              )
            }
          }
          ordensServicosRows = osDataFiltrada.map((os) => {
            const base = {
              id: 'os-' + os.id,
              id_ordem_servico: os.id,
              origem: 'ordem_servico',
              nome_cliente: os.cliente_cadastro?.nome_completo ?? '–',
              data_instalacao: os.data_execucao ? os.data_execucao + 'T00:00:00' : null,
              tipo_servico_id: os.id_tipo_servico,
              tipo_servico: os.tipo_servico ? { nome: os.tipo_servico.nome } : null,
              tipo_padrao_id: null,
              tipo_padrao: null,
              quantidade_modulos: null,
              valor_obra: null,
              valor_material: null,
              deslocamento: null,
              empresa_id: os.id_empresa,
              id_contratante: null,
              id_cliente_cadastro: os.id_cliente,
              status_os: os.status,
              medicao_fechada: false
            }
            const isInstalacao = isInstalacaoNome(os.tipo_servico?.nome)
            const precisaQuantidadeModulos = tipoPorQuantidadeModulos(os.tipo_servico?.nome)
            const isObraCivil = isObraCivilNome(os.tipo_servico?.nome)
            if ((isInstalacao || precisaQuantidadeModulos || isObraCivil) && os.id_cliente) {
              const arrClientes = clientesPorClienteCadastro[os.id_cliente]
              if (isInstalacao) {
                const escolhido = escolherClienteInstalacao(arrClientes, os)
                if (escolhido?.row) {
                  const clienteRow = escolhido.row
                  base.tipo_padrao_id = clienteRow.tipo_padrao_id ?? null
                  base.tipo_padrao = clienteRow.tipo_padrao ? { nome: clienteRow.tipo_padrao.nome } : null
                  base.quantidade_modulos = clienteRow.quantidade_modulos ?? null
                  if (escolhido.mesmoTipo) {
                    base.cliente_instalacao_id = clienteRow.id
                    base.medicao_fechada = !!clienteRow.medicao_fechada
                    base.valor_obra = clienteRow.valor_obra ?? null
                    base.valor_material = clienteRow.valor_material ?? null
                    base.deslocamento = clienteRow.deslocamento != null ? Number(clienteRow.deslocamento) : null
                    base.id_contratante = clienteRow.id_contratante ?? null
                  }
                }
              } else {
                const clienteRow = escolherClienteDoTipo(arrClientes, os)
                if (clienteRow) {
                  base.medicao_fechada = !!clienteRow.medicao_fechada
                  base.cliente_instalacao_id = clienteRow.id
                  base.id_contratante = clienteRow.id_contratante ?? null
                  if (precisaQuantidadeModulos) {
                    base.quantidade_modulos = clienteRow.quantidade_modulos ?? null
                  }
                  if (isObraCivil) {
                    base.valor_obra = clienteRow.valor_obra ?? null
                  }
                }
              }
            }
            return base
          })
        }
      }

      let vistoriaRows = []
      if (!isNonAdminSemEmpresa && !soOutrosTipos && !semCadastroNestaEmpresa) {
        const buildVistoriaQuery = (incluirContratante) => {
          const campos = `
            id,
            id_cliente,
            data_vistoria,
            tipo_padrao_id,
            id_empresa,
            status,
            km_rodado,
            tipo_veiculo,
            medicao_fechada,
            ${incluirContratante ? 'id_contratante,' : ''}
            clientes_cadastro(nome_completo),
            tipo_padrao(nome)
          `
          let q = supabase
            .from('vistorias')
            .select(campos)
            .gte('data_vistoria', inicioDate)
            .lte('data_vistoria', fimDate)
            .order('data_vistoria', { ascending: false })
          if (empresaFiltroId) q = q.in('id_cliente', idsClienteCadastroPorEmpresa)
          return q
        }
        const { data: dataComContratante, error: errComContratante } = await buildVistoriaQuery(true)
        let vistData = dataComContratante
        let vistError = errComContratante
        if (errComContratante) {
          const resFallback = await buildVistoriaQuery(false)
          vistData = resFallback.data
          vistError = resFallback.error
        }
        if (!vistError && vistData && vistData.length > 0) {
          const vistSemCanceladas = vistData.filter((v) => !statusEhCancelado(v.status))
          // Parâmetros de vistoria (valor do km / almoço) por empresa+veículo, para calcular o deslocamento
          const empresaIdsVist = [...new Set(vistSemCanceladas.map((v) => v.id_empresa).filter(Boolean))]
          const paramsVistMap = {}
          if (empresaIdsVist.length > 0) {
            const { data: paramsData } = await supabase
              .from('parametros_vistorias')
              .select('id_empresa, tipo_veiculo, valor_km, km_almoco, valor_almoco')
              .in('id_empresa', empresaIdsVist)
            if (paramsData) {
              for (const p of paramsData) {
                paramsVistMap[`${p.id_empresa}|${p.tipo_veiculo}`] = p
              }
            }
          }
          const calcularDeslocamentoVistoria = (v) => {
            const km = v.km_rodado != null ? Number(v.km_rodado) : 0
            if (!km) return null
            // Abaixo de 100km é considerado moto; acima, usa o veículo informado.
            const veiculo = km >= 100 ? v.tipo_veiculo : 'moto'
            if (!veiculo) return null
            const param = paramsVistMap[`${v.id_empresa}|${veiculo}`]
            if (!param) return null
            const base = (Number(param.valor_km) || 0) * km
            const kmAlmoco = Number(param.km_almoco) || 0
            const aplicaAlmoco = kmAlmoco > 0 && km >= kmAlmoco
            return base + (aplicaAlmoco ? (Number(param.valor_almoco) || 0) : 0)
          }
          vistoriaRows = vistSemCanceladas.map((v) => ({
            id: 'vistoria-' + v.id,
            id_vistoria: v.id,
            origem: 'vistoria',
            nome_cliente: v.clientes_cadastro?.nome_completo ?? '–',
            data_instalacao: v.data_vistoria ? v.data_vistoria + 'T00:00:00' : null,
            tipo_servico_id: vistoriaTipoId || 'vistoria',
            tipo_servico: { nome: 'Vistoria' },
            tipo_padrao_id: v.tipo_padrao_id,
            tipo_padrao: v.tipo_padrao ? { nome: v.tipo_padrao.nome } : null,
            quantidade_modulos: null,
            valor_obra: null,
            valor_material: null,
            km_rodado: v.km_rodado != null ? Number(v.km_rodado) : null,
            tipo_veiculo: v.tipo_veiculo ?? null,
            deslocamento: calcularDeslocamentoVistoria(v),
            empresa_id: v.id_empresa,
            id_contratante: v.id_contratante ?? null,
            id_cliente_cadastro: v.id_cliente,
            valor_servico_vistoria: valorServicoVistoria,
            medicao_fechada: !!v.medicao_fechada
          }))
        }
      }

      const ordemIds = new Set(
        ordensServicosRows
          .map((r) => {
            const raw = r.id_cliente_cadastro ?? r.id_cliente
            if (raw == null) return null
            return typeof raw === 'object' && !(raw instanceof Date) ? (raw.id ?? raw) : raw
          })
          .filter(Boolean)
          .map((x) => String(x).trim().toLowerCase())
      )
      const servicosDataSemDuplicata = servicosData.filter((c) => {
        const raw = c.id_cliente_cadastro ?? c.id_cliente
        const id = raw == null ? '' : (typeof raw === 'object' && !(raw instanceof Date) ? String(raw?.id ?? raw) : String(raw)).trim().toLowerCase()
        return !id || !ordemIds.has(id)
      })
      const combined = [...servicosDataSemDuplicata, ...ordensServicosRows, ...vistoriaRows].sort((a, b) => {
        const da = a.data_instalacao ? new Date(a.data_instalacao).getTime() : 0
        const db = b.data_instalacao ? new Date(b.data_instalacao).getTime() : 0
        return db - da
      })
      const dataChave = (r, comTipo = false) => {
        let rawId = r.id_cliente_cadastro ?? r.id_cliente
        if (rawId != null && typeof rawId === 'object' && !(rawId instanceof Date)) rawId = rawId.id ?? rawId
        const id = (rawId != null ? String(rawId) : '').trim().toLowerCase()
        const d = r.data_instalacao
        let datePart = ''
        if (d) {
          const s = typeof d === 'string' ? d : (d instanceof Date ? d.toISOString() : String(d))
          if (s.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(s)) datePart = s.slice(0, 10)
          else {
            const dt = new Date(d)
            if (!isNaN(dt.getTime())) datePart = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`
          }
        }
        const tipo = (r.tipo_servico?.nome || '').trim().toLowerCase()
        return comTipo ? `${id}|${datePart}|${tipo}` : `${id}|${datePart}`
      }
      const unicosPorChave = new Map()
      combined.forEach((row) => {
        const key = dataChave(row, true)
        if (!key || key === '|' || key.endsWith('||')) {
          unicosPorChave.set(`__semChave_${unicosPorChave.size}`, row)
          return
        }
        const existente = unicosPorChave.get(key)
        const preferirOrdem = row.origem === 'ordem_servico'
        const existenteEOrdem = existente && existente.origem === 'ordem_servico'
        if (!existente || (preferirOrdem && !existenteEOrdem)) unicosPorChave.set(key, row)
      })
      let servicosDataFinal = Array.from(unicosPorChave.values()).sort((a, b) => {
        const da = a.data_instalacao ? new Date(a.data_instalacao).getTime() : 0
        const db = b.data_instalacao ? new Date(b.data_instalacao).getTime() : 0
        return db - da
      })
      const isRetornoNome = (nome) => {
        const n = (nome || '').trim().toLowerCase()
        return n === 'retorno'
      }
      servicosDataFinal = servicosDataFinal.filter((row) => !isRetornoNome(row?.tipo_servico?.nome))
      const tiposMap = new Map()
      ;(tiposList || []).forEach((t) => {
        if (t?.id == null) return
        if (isRetornoNome(t?.nome)) return
        const rawId = String(t.id).trim()
        if (!rawId) return
        const idKey = rawId.length > 10 ? rawId.toLowerCase() : rawId
        tiposMap.set(idKey, t)
      })
      ;(servicosDataFinal || []).forEach((row) => {
        const rawId = row?.tipo_servico_id == null ? '' : String(row.tipo_servico_id).trim()
        const nome = (row?.tipo_servico?.nome || '').trim()
        if (!rawId || !nome || isRetornoNome(nome)) return
        const idKey = rawId.length > 10 ? rawId.toLowerCase() : rawId
        if (!tiposMap.has(idKey)) {
          tiposMap.set(idKey, { id: row.tipo_servico_id, nome, valor_servico: 0 })
        }
      })
      if (![...tiposMap.values()].some(nomeTipoIgualVistoria)) {
        tiposMap.set('vistoria', { id: 'vistoria', nome: 'Vistoria', valor_servico: 0 })
      }
      tiposList = [...tiposMap.values()].sort((a, b) => (a.nome || '').localeCompare((b.nome || ''), 'pt-BR'))
      setTiposServicoFiltroList(tiposList)
      if (filtroTipoServico !== '') {
        const filtroTipo = String(filtroTipoServico).trim().toLowerCase()
        const vistoriaIdNorm = vistoriaTipoId != null ? String(vistoriaTipoId).trim().toLowerCase() : 'vistoria'
        servicosDataFinal = servicosDataFinal.filter((row) => {
          const idTipo = row.tipo_servico_id != null ? String(row.tipo_servico_id).trim().toLowerCase() : ''
          if (idTipo === filtroTipo) return true
          if (row.origem === 'vistoria' && (filtroTipo === vistoriaIdNorm || filtroTipo === 'vistoria')) return true
          return false
        })
      }
      const idsClienteCadastro = [...new Set(servicosDataFinal.map((r) => r.id_cliente_cadastro).filter(Boolean))]
      let kitSolarPorCliente = {}
      const totalFornecidoPorOs = {}
      const totalPorCliente = {}
      const comListaOs = new Set()
      const comListaCliente = new Set()
      if (idsClienteCadastro.length > 0) {
        // Buscar listas de material dos clientes exibidos. Agrupamos tanto por OS (id_ordem_servico)
        // quanto por cliente (fallback para linhas vindas de `clientes`, sem id_ordem_servico).
        const [{ data: kitSolarData }, { data: listasMaterial }] = await Promise.all([
          supabase
            .from('kit_solar_cliente')
            .select('id_cliente, potencia_modulo')
            .in('id_cliente', idsClienteCadastro),
          supabase
            .from('lista_material')
            .select('cliente_id, id_ordem_servico, total_resolve, total_fornecido, ativo')
            .in('cliente_id', idsClienteCadastro)
        ])
        kitSolarPorCliente = (kitSolarData || []).reduce((acc, k) => {
          acc[k.id_cliente] = k.potencia_modulo
          return acc
        }, {})
        // Mesmo total exibido no modal "Lista de material" (total_resolve + total_fornecido), listas ativas.
        ;(listasMaterial || []).forEach((lm) => {
          if (lm.ativo === false) return
          const totalLista = (Number(lm.total_resolve) || 0) + (Number(lm.total_fornecido) || 0)
          if (lm.id_ordem_servico != null) {
            totalFornecidoPorOs[lm.id_ordem_servico] = (totalFornecidoPorOs[lm.id_ordem_servico] || 0) + totalLista
            comListaOs.add(lm.id_ordem_servico)
          }
          if (lm.cliente_id) {
            totalPorCliente[lm.cliente_id] = (totalPorCliente[lm.cliente_id] || 0) + totalLista
            comListaCliente.add(lm.cliente_id)
          }
        })
      }
      setOrdensComListaMaterial(comListaOs)
      setClientesComListaMaterial(comListaCliente)
      setServicos(servicosDataFinal)
      setKitSolarPorClienteCadastro(kitSolarPorCliente)
      setTotalFornecidoPorOrdem(totalFornecidoPorOs)
      setTotalMaterialPorCliente(totalPorCliente)
      let queryContratantes = supabase
        .from('empresas_contratadoras')
        .select('id, nome_empresa, razao_social, cnpj_cpf, endereco, numero, telefone, email')
        .order('nome_empresa')
      if (empresaFiltroId) queryContratantes = queryContratantes.eq('empresa_id', empresaFiltroId)
      const { data: contratantes } = await queryContratantes
      setEmpresasContratadoras(contratantes || [])
      let queryTabelasPreco = supabase
        .from('tabela_precos')
        .select('id, nome_tabela, tipo_cobranca, valor_unitario')
        .order('nome_tabela')
      if (empresaFiltroId) queryTabelasPreco = queryTabelasPreco.eq('id_empresa', empresaFiltroId)
      const { data: tabelasPrecoData } = await queryTabelasPreco
      const list = tabelasPrecoData || []
      setTiposCobrancaList(list)
      if (list.length === 1) {
        setSelectedTabelaPrecoId(String(list[0].id))
      } else {
        setSelectedTabelaPrecoId('')
      }
      setLoading(false)
    } catch (err) {
      setError(err?.message || 'Erro ao carregar serviços.')
      setServicos([])
      setLoading(false)
    }
  }, [filtroDataInicio, filtroDataFim, filtroTipoServico, filtroEmpresa, userRole, userEmpresa?.id])

  const isVistoria = (row) =>
    row.origem === 'vistoria' || (row.tipo_servico?.nome || '').trim().toLowerCase() === 'vistoria'
  const isOrdemServico = (row) => row.origem === 'ordem_servico'
  const temDadosInstalacao = (row) =>
    row.cliente_instalacao_id != null ||
    (isInstalacaoNome(row.tipo_servico?.nome) && (row.valor_obra != null || row.quantidade_modulos != null))
  const isServicoSemValores = (row) =>
    isVistoria(row) || (isOrdemServico(row) && !temDadosInstalacao(row) && getValorServicoTipoRow(row) == null)

  const podeAbrirFotos = (row) => {
    if (isVistoria(row)) return row.id_vistoria != null || row.id_cliente_cadastro != null
    return !!row.id_cliente_cadastro
  }

  const podeAbrirListaMaterial = (row) => temListaMaterial(row)

  const fecharModalDetalheServico = () => {
    setShowModalDetalheServico(false)
    setDetalheServicoTipo(null)
    setDetalheServicoRow(null)
    setDetalheFotosItems([])
    setDetalheFotosExtras({ linkLocalizacao: null })
    setDetalheFotosInfoSecoes([])
    setDetalheListasMaterial([])
    setDetalheServicoLoading(false)
  }

  const abrirModalFotos = async (row) => {
    if (!podeAbrirFotos(row)) return
    setDetalheServicoRow(row)
    setDetalheServicoTipo('fotos')
    setShowModalDetalheServico(true)
    setDetalheServicoLoading(true)
    setDetalheFotosItems([])
    setDetalheFotosExtras({ linkLocalizacao: null })
    setDetalheFotosInfoSecoes([])
    try {
      if (isVistoria(row)) {
        let data = null
        let error = null
        if (row.id_vistoria) {
          const resById = await supabase
            .from('vistorias')
            .select('*, tipo_padrao:tipo_padrao(nome)')
            .eq('id', row.id_vistoria)
            .maybeSingle()
          data = resById.data
          error = resById.error
        } else if (row.id_cliente_cadastro) {
          const resByCliente = await supabase
            .from('vistorias')
            .select('*, tipo_padrao:tipo_padrao(nome)')
            .eq('id_cliente', row.id_cliente_cadastro)
            .order('data_vistoria', { ascending: false })
            .limit(1)
            .maybeSingle()
          data = resByCliente.data
          error = resByCliente.error
        }
        if (!data && row.id_cliente_cadastro) {
          const resByClienteCreated = await supabase
            .from('vistorias')
            .select('*, tipo_padrao:tipo_padrao(nome)')
            .eq('id_cliente', row.id_cliente_cadastro)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle()
          data = resByClienteCreated.data
          error = resByClienteCreated.error
        }
        if (error) throw error
        if (!data) {
          setDetalheFotosItems([])
          setDetalheFotosInfoSecoes([])
          setDetalheFotosExtras({ linkLocalizacao: null })
          return
        }
        setDetalheFotosItems(coletarFotosVistoriaMesmoMetodo(data))
        // Calcular deslocamento (valor_km * km + almoço) a partir dos parâmetros da empresa
        let deslocamentoCalc = row.deslocamento != null ? Number(row.deslocamento) : null
        const kmVist = data?.km_rodado != null ? Number(data.km_rodado) : 0
        // Abaixo de 100km é considerado moto; acima, usa o veículo informado.
        const veiculoVist = kmVist >= 100 ? data?.tipo_veiculo : 'moto'
        if (deslocamentoCalc == null && data?.id_empresa && veiculoVist && kmVist > 0) {
          const { data: paramVist } = await supabase
            .from('parametros_vistorias')
            .select('valor_km, km_almoco, valor_almoco')
            .eq('id_empresa', data.id_empresa)
            .eq('tipo_veiculo', veiculoVist)
            .maybeSingle()
          if (paramVist) {
            const km = kmVist
            const base = (Number(paramVist.valor_km) || 0) * km
            const kmAlmoco = Number(paramVist.km_almoco) || 0
            const aplicaAlmoco = kmAlmoco > 0 && km >= kmAlmoco
            deslocamentoCalc = base + (aplicaAlmoco ? (Number(paramVist.valor_almoco) || 0) : 0)
          }
        }
        setDetalheFotosInfoSecoes([{ titulo: 'Informações da vistoria', linhas: montarLinhasInfoVistoria(data, deslocamentoCalc) }])
        let linkLoc = null
        if (data?.id_cliente) {
          const { data: cadL } = await supabase
            .from('clientes_cadastro')
            .select('link_localizacao')
            .eq('id', data.id_cliente)
            .maybeSingle()
          const link = cadL?.link_localizacao != null ? String(cadL.link_localizacao).trim() : ''
          if (link) linkLoc = link
        }
        setDetalheFotosExtras({ linkLocalizacao: linkLoc })
        return
      }
      if (row.id_cliente_cadastro) {
        const ehInstalacao = isInstalacaoNome(row.tipo_servico?.nome)
        let idClienteDetalhe = row.cliente_instalacao_id || null
        if (
          !idClienteDetalhe &&
          !isOrdemServico(row) &&
          !isVistoria(row) &&
          row.id &&
          typeof row.id === 'string' &&
          !row.id.startsWith('vistoria-') &&
          !row.id.startsWith('os-')
        ) {
          idClienteDetalhe = row.id
        }
        if (!idClienteDetalhe) {
          let cliQuery = supabase
            .from('clientes')
            .select('id, data_instalacao, tipo_servico_id')
            .eq('id_cliente_cadastro', row.id_cliente_cadastro)
          if (row.tipo_servico_id) cliQuery = cliQuery.eq('tipo_servico_id', row.tipo_servico_id)
          const { data: cliCands, error: errCliByCadastro } = await cliQuery.order('data_instalacao', { ascending: false })
          if (errCliByCadastro) throw errCliByCadastro
          const lista = cliCands || []
          if (lista.length === 1) {
            idClienteDetalhe = lista[0].id
          } else if (lista.length > 1) {
            idClienteDetalhe = escolherClienteDoTipo(lista, {
              id_tipo_servico: row.tipo_servico_id,
              data_execucao: row.data_instalacao
            })?.id ?? lista[0].id
          }
        }

        let cli = null
        if (idClienteDetalhe) {
          const { data: cliData, error: errCli } = await supabase
            .from('clientes')
            .select('*')
            .eq('id', idClienteDetalhe)
            .maybeSingle()
          if (errCli) throw errCli
          cli = cliData
          if (cli && row.tipo_servico_id && String(cli.tipo_servico_id) !== String(row.tipo_servico_id)) {
            cli = null
            idClienteDetalhe = null
          }
        }

        const itens = []
        if (ehInstalacao && idClienteDetalhe) {
          let fotosClientesRows = []
          const { data: fotosByClienteId, error: errFotosByClienteId } = await supabase
            .from('fotos_clientes')
            .select('*')
            .eq('cliente_id', idClienteDetalhe)
          if (!errFotosByClienteId) {
            fotosClientesRows = fotosByClienteId || []
          } else {
            const { data: fotosByIdCliente, error: errFotosByIdCliente } = await supabase
              .from('fotos_clientes')
              .select('*')
              .eq('id_cliente', idClienteDetalhe)
            if (errFotosByIdCliente) throw errFotosByIdCliente
            fotosClientesRows = fotosByIdCliente || []
          }
          for (const fotoRow of fotosClientesRows) {
            for (const it of extrairFotosRegistroGenerico(fotoRow, 'Fachadas')) {
              if (!itens.some((x) => x.url === it.url)) itens.push(it)
            }
          }
        }

        let fotosBaixaRows = []
        const { data: baixaByClienteId, error: errBaixaByClienteId } = await supabase
          .from('formularios_baixa_resolve')
          .select('*')
          .eq('cliente_id', row.id_cliente_cadastro)
        if (!errBaixaByClienteId) {
          fotosBaixaRows = baixaByClienteId || []
        } else {
          const { data: baixaByIdCliente, error: errBaixaByIdCliente } = await supabase
            .from('formularios_baixa_resolve')
            .select('*')
            .eq('id_cliente', row.id_cliente_cadastro)
          if (errBaixaByIdCliente) throw errBaixaByIdCliente
          fotosBaixaRows = baixaByIdCliente || []
        }
        const nomeTipoRow = row.tipo_servico?.nome
        fotosBaixaRows = (fotosBaixaRows || []).filter((b) =>
          nomeTipoRow ? mesmoNomeTipo(b.tipo_formulario, nomeTipoRow) : false
        )
        for (const baixaRow of fotosBaixaRows) {
          for (const it of extrairFotosFormularioBaixaResolve(baixaRow)) {
            if (!itens.some((x) => x.url === it.url)) itens.push({ ...it, label: `${it.label} (baixa)` })
          }
        }
        if (ehInstalacao) {
          const facUrl = resolverImagemPublica(cli?.foto_fachada, 'Fachadas')
          if (facUrl && !itens.some((x) => x.url === facUrl)) itens.push({ label: 'Fachada (clientes)', url: facUrl })
        }
        setDetalheFotosItems(itens)

        const link = cli?.link_localizacao != null ? String(cli.link_localizacao).trim() : ''
        setDetalheFotosExtras({
          linkLocalizacao: link || null
        })

        const secoes = []
        if (cli) {
          const cliInfo = {
            ...cli,
            nome_completo: cli.nome_completo || cli.nome_cliente || row.nome_cliente || null
          }
          const linhasC = montarLinhasInfoCadastroCliente(cliInfo)
          if (linhasC.length) secoes.push({ titulo: 'Dados do cliente (clientes)', linhas: linhasC })
        }
        let valorMaterialLista = 0
        if (row.id_ordem_servico != null || row.id_cliente_cadastro) {
          let lmQuery = supabase
            .from('lista_material')
            .select('total_resolve, total_fornecido, ativo')
          lmQuery = row.id_ordem_servico != null
            ? lmQuery.eq('id_ordem_servico', row.id_ordem_servico)
            : lmQuery.eq('cliente_id', row.id_cliente_cadastro)
          const { data: listaMaterialRows, error: errListaMaterial } = await lmQuery
          if (!errListaMaterial && listaMaterialRows) {
            valorMaterialLista = listaMaterialRows
              .filter((item) => item.ativo !== false)
              .reduce((acc, item) => acc + (Number(item.total_resolve) || 0) + (Number(item.total_fornecido) || 0), 0)
          }
        }
        const dadosServico = {
          ...(cli || {}),
          tipo_servico: row.tipo_servico || cli?.tipo_servico,
          valor_obra: ehInstalacao || isObraCivilNome(row.tipo_servico?.nome) ? (cli?.valor_obra ?? row.valor_obra) : null,
          valor_material: valorMaterialLista
        }
        const linhasI = montarLinhasInfoClienteInstalacao(dadosServico)
        if (linhasI.length) {
          secoes.push({
            titulo: ehInstalacao ? 'Dados da instalação' : `Dados de ${row.tipo_servico?.nome || 'serviço'}`,
            linhas: linhasI
          })
        }
        setDetalheFotosInfoSecoes(secoes)
        return
      }
    } catch (err) {
      console.error(err)
      setDetalheFotosItems([])
      setDetalheFotosInfoSecoes([])
    } finally {
      setDetalheServicoLoading(false)
    }
  }

  const abrirModalListaMaterial = async (row) => {
    if (!podeAbrirListaMaterial(row)) return
    setDetalheServicoRow(row)
    setDetalheServicoTipo('lista')
    setShowModalDetalheServico(true)
    setDetalheServicoLoading(true)
    setDetalheListasMaterial([])
    try {
      let listaQuery = supabase
        .from('lista_material')
        .select('id, observacoes, data_criacao, ativo, data_atualizacao, total_resolve, total_fornecido')
        .order('data_atualizacao', { ascending: false })
      // Linhas com OS: filtra pela OS; linhas sem OS (vindas de `clientes`): filtra pelo cliente.
      listaQuery = row.id_ordem_servico != null
        ? listaQuery.eq('id_ordem_servico', row.id_ordem_servico)
        : listaQuery.eq('cliente_id', row.id_cliente_cadastro)
      const { data: listas, error } = await listaQuery
      if (error) throw error
      const ativas = (listas || []).filter((l) => l.ativo !== false)
      const ids = ativas.map((l) => l.id)
      const itensPorLista = {}
      if (ids.length > 0) {
        const { data: itensRows, error: errItens } = await supabase.from('itens_material').select('*').in('lista_material_id', ids)
        if (errItens) throw errItens
        for (const it of itensRows || []) {
          const lid = it.lista_material_id
          if (!itensPorLista[lid]) itensPorLista[lid] = []
          itensPorLista[lid].push(it)
        }
      }
      setDetalheListasMaterial(
        ativas.map((lista) => ({
          ...lista,
          itens: itensPorLista[lista.id] || []
        }))
      )
    } catch (err) {
      console.error(err)
      setDetalheListasMaterial([])
    } finally {
      setDetalheServicoLoading(false)
    }
  }

  useEffect(() => {
    loadServicos()
  }, [loadServicos])

  useEffect(() => {
    if (!selectedTabelaPrecoId || !tiposCobrancaList.length) return
    const selected = tiposCobrancaList.find((t) => t.id === parseInt(selectedTabelaPrecoId, 10))
    if (!selected || selected.tipo_cobranca !== 'Kwp') {
      setFaixasKwpSelecionada([])
      return
    }
    const tabelaId = parseInt(selectedTabelaPrecoId, 10)
    if (isNaN(tabelaId)) {
      setFaixasKwpSelecionada([])
      return
    }
    let cancelled = false
    ;(async () => {
      const { data, error: err } = await supabase
        .from('faixas_kwp')
        .select('id, tabela_preco_id, kwp_min, kwp_max, valor')
        .eq('tabela_preco_id', tabelaId)
        .order('kwp_min', { ascending: true })
      if (cancelled) return
      if (err) {
        console.error('Erro ao carregar faixas Kwp:', err)
        setFaixasKwpSelecionada([])
        return
      }
      setFaixasKwpSelecionada(data || [])
    })()
    return () => { cancelled = true }
  }, [selectedTabelaPrecoId, tiposCobrancaList])

  useEffect(() => {
    if (!selectedTabelaPrecoId || !tiposCobrancaList.length) {
      setValorServicoPorTipoTabela({})
      return
    }
    const selected = tiposCobrancaList.find((t) => t.id === parseInt(selectedTabelaPrecoId, 10))
    if (!selected) {
      setValorServicoPorTipoTabela({})
      return
    }
    const tabelaId = parseInt(selectedTabelaPrecoId, 10)
    if (isNaN(tabelaId)) {
      setValorServicoPorTipoTabela({})
      return
    }
    let cancelled = false
    ;(async () => {
      const { data, error } = await supabase
        .from('tabela_precos_servicos')
        .select('id_tipo_servico, valor, tipo_servico:tipo_servico(nome)')
        .eq('id_tabela_preco', tabelaId)
      if (cancelled) return
      if (error) {
        console.error('Erro ao carregar preços por tipo de serviço:', error)
        setValorServicoPorTipoTabela({})
        return
      }
      const map = {}
      ;(data || []).forEach((r) => {
        const valor = Number(r.valor) || 0
        const raw = r.id_tipo_servico != null ? String(r.id_tipo_servico).trim() : ''
        const key = raw ? (raw.length > 10 ? raw.toLowerCase() : raw) : ''
        if (key) map[key] = valor
        const nomeTipo = (r.tipo_servico?.nome || '').trim().toLowerCase()
        if (nomeTipo === 'vistoria') map._vistoria = valor
      })
      setValorServicoPorTipoTabela(map)
    })()
    return () => { cancelled = true }
  }, [selectedTabelaPrecoId, tiposCobrancaList])

  useEffect(() => {
    if (!selectedTabelaPrecoId) {
      setValorPadraoPorTipoPadrao({})
      return
    }
    const tabelaId = parseInt(selectedTabelaPrecoId, 10)
    if (isNaN(tabelaId)) {
      setValorPadraoPorTipoPadrao({})
      return
    }
    let cancelled = false
    ;(async () => {
      const { data, error: err } = await supabase
        .from('tipos_padrao_cobranca')
        .select('id_padrao, valor')
        .eq('tabela_preco_id', tabelaId)
      if (cancelled) return
      if (err) {
        console.error('Erro ao carregar tipos padrão cobrança:', err)
        setValorPadraoPorTipoPadrao({})
        return
      }
      const map = {}
      ;(data || []).forEach((r) => {
        const key = r.id_padrao == null ? '' : String(r.id_padrao)
        map[key] = Number(r.valor) || 0
      })
      setValorPadraoPorTipoPadrao(map)
    })()
    return () => { cancelled = true }
  }, [selectedTabelaPrecoId])

  const formatarData = (dataString) => {
    if (!dataString) return '–'
    try {
      const s = String(dataString).split('T')[0].trim()
      const [ano, mes, dia] = s.split('-')
      if (ano && mes && dia) return `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}/${ano}`
    } catch (_) {}
    return '–'
  }

  const formatarDataRecibo = (row) => {
    const raw = row?.data_instalacao ?? row?.data_execucao ?? null
    const f = formatarData(raw)
    return f !== '–' ? f : 'N/A'
  }

  const formatarMoeda = (v) => {
    if (v == null || v === '' || isNaN(Number(v))) return '–'
    return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
  }

  const qtdModulo = (row) => {
    const v = row.quantidade_modulos
    if (v != null && v !== '') return v
    return '–'
  }

  /** Total Módulos = Qtd. módulo * valor módulo (da tabela de preço, quando tipo for por módulo) */
  const getTotalModulosRow = (row) => {
    if (!selectedTabelaPreco || selectedTabelaPreco.tipo_cobranca !== 'Modulo') return null
    const qtd = row.quantidade_modulos != null && row.quantidade_modulos !== '' ? Number(row.quantidade_modulos) : 0
    const tipoNome = (row.tipo_servico?.nome || '').trim().toLowerCase()
    const isDesinstReinst = tipoNome === 'desinstalação/reinstalação' || tipoNome === 'desinstalacao/reinstalacao'
    if (isDesinstReinst) {
      const rawId = row.tipo_servico_id == null ? '' : String(row.tipo_servico_id).trim()
      const key = rawId && rawId.length > 10 ? rawId.toLowerCase() : rawId
      const valorPorTipo = key ? (Number(valorServicoPorTipoTabela[key]) || 0) : 0
      return qtd * valorPorTipo
    }
    const valorModulo = Number(selectedTabelaPreco.valor_unitario) || 0
    return qtd * valorModulo
  }

  const cepDigits = (v) => (v || '').replace(/\D/g, '').slice(0, 8)
  const cepDisplay = (v) => {
    const d = cepDigits(v)
    if (d.length <= 5) return d
    return `${d.slice(0, 5)}-${d.slice(5)}`
  }

  const buscarCepContratadora = async () => {
    const digits = cepDigits(formContratadora.cep)
    if (digits.length !== 8) {
      showErroValidacao('CEP inválido. Digite 8 dígitos.')
      return
    }
    setMsgContratadora({ type: '', text: '' })
    setSearchCepContratadora(true)
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`)
      const data = await res.json()
      if (data.erro) {
        showErroValidacao('CEP não encontrado.')
        return
      }
      setFormContratadora(prev => ({
        ...prev,
        endereco: data.logradouro || prev.endereco,
        bairro: data.bairro || prev.bairro,
        cidade: data.localidade || prev.cidade,
        estado: (data.uf || prev.estado).toUpperCase().slice(0, 2)
      }))
      setMsgContratadora({ type: 'success', text: 'Endereço encontrado!' })
    } catch (err) {
      console.error('Erro ao buscar CEP:', err)
      showErroValidacao('Erro ao buscar CEP. Tente novamente.')
    } finally {
      setSearchCepContratadora(false)
    }
  }

  const handleCepChange = (e) => {
    const raw = e.target.value
    const digits = cepDigits(raw)
    setFormContratadora(prev => ({ ...prev, cep: digits }))
  }

  const handleCepBlur = () => {
    if (cepDigits(formContratadora.cep).length === 8) buscarCepContratadora()
  }

  const openModalContratadora = () => {
    setFormContratadora(INIT_CONTRATADORA)
    setTipoCadastroContratadora('cnpj')
    setMsgContratadora({ type: '', text: '' })
    setSearchCepContratadora(false)
    setShowModalContratadora(true)
  }

  const closeModalContratadora = () => {
    setShowModalContratadora(false)
    setFormContratadora(INIT_CONTRATADORA)
    setTipoCadastroContratadora('cnpj')
    setMsgContratadora({ type: '', text: '' })
    setSearchCepContratadora(false)
  }

  const handleTipoCadastroChange = (tipo) => {
    setTipoCadastroContratadora(tipo)
    if (tipo === 'cpf') setFormContratadora(prev => ({ ...prev, razao_social: '' }))
  }

  const handleContratanteChange = async (row, value) => {
    if (row.origem === 'ordem_servico' && row.cliente_instalacao_id == null) {
      setAtualizandoContratante(null)
      return
    }
    const id = value === '' ? null : parseInt(value, 10)
    setAtualizandoContratante(row.id)
    try {
      if (row.origem === 'vistoria' && row.id_vistoria != null) {
        const { error: updError } = await supabase
          .from('vistorias')
          .update({ id_contratante: id })
          .eq('id', row.id_vistoria)
        if (updError) {
          const msg = updError.message || ''
          if (msg.includes('id_contratante') || msg.includes('schema cache')) {
            setError('A coluna "Contratante" ainda não existe na tabela vistorias. Execute a migração 75 (database/75_add_id_contratante_to_vistorias.sql) no Supabase para habilitar.')
            setServicos(prev => prev.map((r) => (r.id === row.id ? { ...r, id_contratante: id } : r)))
            return
          }
          throw updError
        }
      } else if (row.origem === 'ordem_servico' && row.cliente_instalacao_id != null) {
        const { error: updError } = await supabase
          .from('clientes')
          .update({ id_contratante: id })
          .eq('id', row.cliente_instalacao_id)
        if (updError) throw updError
      } else {
        const { error: updError } = await supabase
          .from('clientes')
          .update({ id_contratante: id })
          .eq('id', row.id)
        if (updError) throw updError
      }
      setServicos(prev => prev.map((r) => (r.id === row.id ? { ...r, id_contratante: id } : r)))
    } catch (err) {
      console.error('Erro ao atualizar contratante:', err)
      setError(err.message || 'Erro ao atualizar contratante.')
    } finally {
      setAtualizandoContratante(null)
    }
  }

  const handleSubmitContratadora = async (e) => {
    e.preventDefault()
    setSavingContratadora(true)
    setMsgContratadora({ type: '', text: '' })
    try {
      const cepVal = cepDigits(formContratadora.cep)
      const payload = {
        empresa_id: userEmpresa?.id || null,
        nome_empresa: formContratadora.nome_empresa.trim() || null,
        razao_social: tipoCadastroContratadora === 'cpf' ? null : (formContratadora.razao_social.trim() || null),
        cnpj_cpf: formContratadora.cnpj_cpf.trim() || null,
        endereco: formContratadora.endereco.trim() || null,
        numero: formContratadora.numero.trim() || null,
        cep: cepVal || null,
        bairro: formContratadora.bairro.trim() || null,
        cidade: formContratadora.cidade.trim() || null,
        estado: formContratadora.estado.trim() || null,
        telefone: formContratadora.telefone.trim() || null,
        email: formContratadora.email.trim() || null
      }
      const { error: insertError } = await supabase
        .from('empresas_contratadoras')
        .insert([payload])
      if (insertError) throw insertError
      closeModalContratadora()
      setShowConfirmacaoCadastro(true)
      loadServicos()
    } catch (err) {
      console.error('Erro ao cadastrar empresa contratadora:', err)
      showErroValidacao(err.message || 'Erro ao cadastrar.')
    } finally {
      setSavingContratadora(false)
    }
  }

  const openModalPadraoCobranca = async () => {
    setFormPadraoCobranca({ tabela_preco_id: '', id_padrao: '', valor: '' })
    setMsgPadraoCobranca({ type: '', text: '' })
    setListaPadroesCobrancaTabela([])
    setValorEditPadrao({})
    setIdPadraoEditPadrao({})
    setShowModalPadraoCobranca(true)
    try {
      let queryTabela = supabase
        .from('tabela_precos')
        .select('id, nome_tabela, tipo_cobranca')
        .order('id', { ascending: false })
      if (userEmpresa?.id) {
        queryTabela = queryTabela.eq('id_empresa', userEmpresa.id)
      }
      const { data: tabelaData } = await queryTabela
      setTabelaPrecosList(tabelaData || [])
      const { data: padraoData } = await supabase
        .from('tipo_padrao')
        .select('id, nome')
        .order('nome')
      setTipoPadraoList(padraoData || [])
    } catch (err) {
      console.error('Erro ao carregar dados do modal:', err)
      showErroValidacao(err.message || 'Erro ao carregar dados.')
    }
  }

  const closeModalPadraoCobranca = () => {
    setShowModalPadraoCobranca(false)
    setFormPadraoCobranca({ tabela_preco_id: '', id_padrao: '', valor: '' })
    setMsgPadraoCobranca({ type: '', text: '' })
    setListaPadroesCobrancaTabela([])
    setValorEditPadrao({})
    setIdPadraoEditPadrao({})
    setAtualizandoValorPadrao(null)
    setExcluindoPadrao(null)
  }

  const loadPadroesCobrancaTabela = async (tabelaPrecoId) => {
    setFormPadraoCobranca(prev => ({ ...prev, tabela_preco_id: tabelaPrecoId }))
    if (!tabelaPrecoId) {
      setListaPadroesCobrancaTabela([])
      setValorEditPadrao({})
      setIdPadraoEditPadrao({})
      return
    }
    const id = parseInt(tabelaPrecoId, 10)
    if (isNaN(id)) {
      setListaPadroesCobrancaTabela([])
      return
    }
    try {
      const { data, error: err } = await supabase
        .from('tipos_padrao_cobranca')
        .select('id, tabela_preco_id, id_padrao, valor, tipo_padrao:tipo_padrao(nome)')
        .eq('tabela_preco_id', id)
      if (err) throw err
      const lista = data || []
      setListaPadroesCobrancaTabela(lista)
      const valorMap = {}
      const idPadraoMap = {}
      lista.forEach((r) => {
        valorMap[r.id] = r.valor != null ? String(r.valor) : ''
        idPadraoMap[r.id] = r.id_padrao != null ? String(r.id_padrao) : ''
      })
      setValorEditPadrao(valorMap)
      setIdPadraoEditPadrao(idPadraoMap)
    } catch (err) {
      console.error('Erro ao carregar tipos padrão cobrança:', err)
      showErroValidacao('Erro ao carregar tipos de padrão da tabela.')
      setListaPadroesCobrancaTabela([])
    }
  }

  const handleAtualizarPadrao = async (registroId) => {
    const tabelaPrecoId = parseInt(formPadraoCobranca.tabela_preco_id, 10)
    const idPadraoNovo = (idPadraoEditPadrao[registroId] ?? '').toString().trim() || null
    const valorStr = valorEditPadrao[registroId]
    const valor = valorStr === '' ? 0 : parseFloat(String(valorStr).replace(',', '.')) || 0
    if (!idPadraoNovo) {
      showErroValidacao('Selecione o tipo de padrão.')
      return
    }
    setAtualizandoValorPadrao(registroId)
    try {
      const { data: existente } = await supabase
        .from('tipos_padrao_cobranca')
        .select('id')
        .eq('tabela_preco_id', tabelaPrecoId)
        .eq('id_padrao', idPadraoNovo)
        .neq('id', registroId)
        .maybeSingle()
      if (existente) {
        setAtualizandoValorPadrao(null)
        showErroValidacao('Este tipo de padrão já está cadastrado para esta tabela de preço.')
        return
      }
      const { error: err } = await supabase
        .from('tipos_padrao_cobranca')
        .update({ id_padrao: idPadraoNovo, valor, updated_at: new Date().toISOString() })
        .eq('id', registroId)
      if (err) throw err
      const tipoPadrao = tipoPadraoList.find((p) => String(p.id) === idPadraoNovo)
      setListaPadroesCobrancaTabela(prev => prev.map((r) => (r.id === registroId ? { ...r, id_padrao: idPadraoNovo, valor, tipo_padrao: tipoPadrao ? { nome: tipoPadrao.nome } : r.tipo_padrao } : r)))
      setValorEditPadrao(prev => ({ ...prev, [registroId]: valorStr }))
      setIdPadraoEditPadrao(prev => ({ ...prev, [registroId]: idPadraoNovo }))
      setMsgConfirmacaoPadrao('Registro atualizado com sucesso.')
      setShowConfirmacaoPadraoCobranca(true)
    } catch (err) {
      console.error('Erro ao atualizar:', err)
      showErroValidacao(err.message || 'Erro ao atualizar.')
    } finally {
      setAtualizandoValorPadrao(null)
    }
  }

  const handleExcluirPadrao = async (registroId) => {
    setExcluindoPadrao(registroId)
    try {
      const { error: err } = await supabase
        .from('tipos_padrao_cobranca')
        .delete()
        .eq('id', registroId)
      if (err) throw err
      setListaPadroesCobrancaTabela(prev => prev.filter((r) => r.id !== registroId))
      setValorEditPadrao(prev => {
        const next = { ...prev }
        delete next[registroId]
        return next
      })
      setIdPadraoEditPadrao(prev => {
        const next = { ...prev }
        delete next[registroId]
        return next
      })
      setMsgConfirmacaoPadrao('Tipo de padrão removido com sucesso.')
      setShowConfirmacaoPadraoCobranca(true)
    } catch (err) {
      console.error('Erro ao excluir:', err)
      showErroValidacao(err.message || 'Erro ao excluir.')
    } finally {
      setExcluindoPadrao(null)
    }
  }

  const handleSubmitPadraoCobranca = async (e) => {
    e.preventDefault()
    setSavingPadraoCobranca(true)
    setMsgPadraoCobranca({ type: '', text: '' })
    try {
      const tabelaPrecoId = formPadraoCobranca.tabela_preco_id === '' ? null : parseInt(formPadraoCobranca.tabela_preco_id, 10)
      if (!tabelaPrecoId) {
        setSavingPadraoCobranca(false)
        showErroValidacao('Selecione a tabela de preço.')
        return
      }
      const idPadrao = formPadraoCobranca.id_padrao?.trim() || null
      if (!idPadrao) {
        setSavingPadraoCobranca(false)
        showErroValidacao('Selecione o tipo de padrão.')
        return
      }
      const { data: existente } = await supabase
        .from('tipos_padrao_cobranca')
        .select('id')
        .eq('tabela_preco_id', tabelaPrecoId)
        .eq('id_padrao', idPadrao)
        .maybeSingle()
      if (existente) {
        setSavingPadraoCobranca(false)
        showErroValidacao('Este tipo de padrão já está cadastrado para esta tabela de preço.')
        return
      }
      const valor = formPadraoCobranca.valor === '' ? 0 : parseFloat(String(formPadraoCobranca.valor).replace(',', '.')) || 0
      const payload = {
        tabela_preco_id: tabelaPrecoId,
        id_padrao: idPadrao,
        valor
      }
      const { error: insertError } = await supabase
        .from('tipos_padrao_cobranca')
        .insert([payload])
      if (insertError) throw insertError
      setMsgConfirmacaoPadrao('Tipo padrão cobrança cadastrado com sucesso.')
      setShowConfirmacaoPadraoCobranca(true)
      setFormPadraoCobranca(prev => ({ ...prev, id_padrao: '', valor: '' }))
      loadPadroesCobrancaTabela(String(tabelaPrecoId))
    } catch (err) {
      console.error('Erro ao cadastrar tipo padrão cobrança:', err)
      showErroValidacao(err.message || 'Erro ao cadastrar.')
    } finally {
      setSavingPadraoCobranca(false)
    }
  }

  const openModalTabelaPrecos = async () => {
    setFormTabelaPrecos({ nome_tabela: '', tipo_cobranca: 'Modulo', valor_unitario: '', tabela_preco_id_edicao: '' })
    setFaixasKwp([])
    setCurrentFaixaKwp({ kwp_min: '', kwp_max: '', valor: '' })
    setPrecosServicosPorTipo({})
    setMsgTabelaPrecos({ type: '', text: '' })
    setShowModalTabelaPrecos(true)
    try {
      const { data } = await supabase.from('tipo_servico').select('id, nome').order('nome')
      setTiposServicoList(data || [])
    } catch (err) {
      console.error('Erro ao carregar tipos de serviço:', err)
    }
  }

  const closeModalTabelaPrecos = () => {
    setShowModalTabelaPrecos(false)
    setFormTabelaPrecos({ nome_tabela: '', tipo_cobranca: 'Modulo', valor_unitario: '', tabela_preco_id_edicao: '' })
    setFaixasKwp([])
    setCurrentFaixaKwp({ kwp_min: '', kwp_max: '', valor: '' })
    setPrecosServicosPorTipo({})
    setTiposServicoList([])
    setMsgTabelaPrecos({ type: '', text: '' })
  }

  const handleSelecionarTabelaModuloEdicao = async (tabelaPrecoId) => {
    const id = tabelaPrecoId === '' ? null : parseInt(tabelaPrecoId, 10)
    setFormTabelaPrecos(prev => ({
      ...prev,
      tabela_preco_id_edicao: tabelaPrecoId,
      nome_tabela: id ? (tiposCobrancaList.find(t => t.id === id)?.nome_tabela ?? '') : '',
      valor_unitario: id ? (tiposCobrancaList.find(t => t.id === id)?.valor_unitario ?? '') : prev.valor_unitario
    }))
    setPrecosServicosPorTipo({})
    if (!id) return
    try {
      const { data, error } = await supabase
        .from('tabela_precos_servicos')
        .select('id_tipo_servico, valor')
        .eq('id_tabela_preco', id)
      if (error) throw error
      const mapa = {}
      ;(data || []).forEach((r) => {
        mapa[r.id_tipo_servico] = r.valor != null ? String(r.valor) : ''
      })
      setPrecosServicosPorTipo(mapa)
    } catch (err) {
      console.error('Erro ao carregar preços por tipo de serviço:', err)
      showErroValidacao('Erro ao carregar preços dos tipos de serviço.')
    }
  }

  const handleSelecionarTabelaKwpEdicao = async (tabelaPrecoId) => {
    const id = tabelaPrecoId === '' ? null : parseInt(tabelaPrecoId, 10)
    setFormTabelaPrecos(prev => ({ ...prev, tabela_preco_id_edicao: tabelaPrecoId, nome_tabela: id ? (tiposCobrancaList.find(t => t.id === id)?.nome_tabela ?? '') : '' }))
    setFaixasKwp([])
    if (!id) return
    setMsgTabelaPrecos({ type: '', text: '' })
    try {
      const { data, error: err } = await supabase
        .from('faixas_kwp')
        .select('id, kwp_min, kwp_max, valor')
        .eq('tabela_preco_id', id)
        .order('kwp_min', { ascending: true })
      if (err) throw err
      const faixas = (data || []).map(f => ({
        kwp_min: f.kwp_min,
        kwp_max: f.kwp_max,
        valor: f.valor
      }))
      setFaixasKwp(faixas)
    } catch (err) {
      console.error('Erro ao carregar faixas da tabela:', err)
      showErroValidacao('Erro ao carregar faixas da tabela.')
    }
  }

  const addFaixaKwp = () => {
    const kwpMinStr = String(currentFaixaKwp.kwp_min).trim()
    const kwpMaxStr = String(currentFaixaKwp.kwp_max).trim()
    if (!kwpMinStr || !kwpMaxStr) {
      showErroValidacao('Preencha Kwp mín e Kwp máx para adicionar a faixa.')
      return
    }
    const newMin = parseFloat(String(currentFaixaKwp.kwp_min).replace(',', '.')) || 0
    const newMax = parseFloat(String(currentFaixaKwp.kwp_max).replace(',', '.')) || 0
    if (newMin > newMax) {
      showErroValidacao('Kwp mín não pode ser maior que Kwp máx.')
      return
    }
    const overlap = faixasKwp.some((f) => {
      const eMin = parseFloat(String(f.kwp_min).replace(',', '.')) || 0
      const eMax = parseFloat(String(f.kwp_max).replace(',', '.')) || 0
      return newMin <= eMax && newMax >= eMin
    })
    if (overlap) {
      showErroValidacao('O intervalo de Kwp (mín–máx) não pode sobrepor ou estar entre as faixas já adicionadas.')
      return
    }
    setMsgTabelaPrecos({ type: '', text: '' })
    setFaixasKwp(prev => [...prev, { kwp_min: currentFaixaKwp.kwp_min, kwp_max: currentFaixaKwp.kwp_max, valor: currentFaixaKwp.valor }])
    setCurrentFaixaKwp({ kwp_min: '', kwp_max: '', valor: '' })
  }

  const removeFaixaKwp = (index) => {
    setFaixasKwp(prev => prev.filter((_, i) => i !== index))
  }

  const handleSubmitTabelaPrecos = async (e) => {
    e.preventDefault()
    setSavingTabelaPrecos(true)
    setMsgTabelaPrecos({ type: '', text: '' })
    try {
      const idEmpresa = userEmpresa?.id || null
      if (!idEmpresa) {
        setSavingTabelaPrecos(false)
        showErroValidacao('Nenhuma empresa selecionada no contexto.')
        return
      }
      const tipoCobranca = formTabelaPrecos.tipo_cobranca || 'Modulo'

      const nomeTabela = formTabelaPrecos.nome_tabela?.trim() || null
      if (tipoCobranca === 'Modulo') {
        const valorUnitario = formTabelaPrecos.valor_unitario === '' ? 0 : parseFloat(String(formTabelaPrecos.valor_unitario).replace(',', '.')) || 0
        const idEdicaoModulo = formTabelaPrecos.tabela_preco_id_edicao ? parseInt(formTabelaPrecos.tabela_preco_id_edicao, 10) : null
        let tabelaPrecoId

        if (idEdicaoModulo) {
          const { error: updateError } = await supabase
            .from('tabela_precos')
            .update({ nome_tabela: nomeTabela, valor_unitario: valorUnitario, updated_at: new Date().toISOString() })
            .eq('id', idEdicaoModulo)
          if (updateError) throw updateError
          tabelaPrecoId = idEdicaoModulo

          await supabase.from('tabela_precos_servicos').delete().eq('id_tabela_preco', idEdicaoModulo)
          for (const tipo of tiposServicoList) {
            const valorStr = precosServicosPorTipo[tipo.id]
            if (valorStr !== undefined && valorStr !== '' && valorStr !== null) {
              const valor = parseFloat(String(valorStr).replace(',', '.')) || 0
              await supabase.from('tabela_precos_servicos').insert([{
                id_tabela_preco: idEdicaoModulo,
                id_tipo_servico: tipo.id,
                valor
              }])
            }
          }
        } else {
          const { error: insertError } = await supabase
            .from('tabela_precos')
            .insert([{
              nome_tabela: nomeTabela,
              tipo_cobranca: 'Modulo',
              valor_unitario: valorUnitario,
              id_empresa: idEmpresa
            }])
          if (insertError) throw insertError
        }
      } else if (tipoCobranca === 'Kwp') {
        if (faixasKwp.length === 0) {
          setSavingTabelaPrecos(false)
          showErroValidacao('Adicione ao menos uma faixa Kwp na tabela.')
          return
        }
        const faixasValidas = faixasKwp
        const idEdicao = formTabelaPrecos.tabela_preco_id_edicao ? parseInt(formTabelaPrecos.tabela_preco_id_edicao, 10) : null
        let tabelaPrecoId
        if (idEdicao) {
          const { error: updateError } = await supabase
            .from('tabela_precos')
            .update({ nome_tabela: nomeTabela, updated_at: new Date().toISOString() })
            .eq('id', idEdicao)
          if (updateError) throw updateError
          const { error: deleteError } = await supabase.from('faixas_kwp').delete().eq('tabela_preco_id', idEdicao)
          if (deleteError) throw deleteError
          tabelaPrecoId = idEdicao
        } else {
          const { data: inserted, error: insertTabelaError } = await supabase
            .from('tabela_precos')
            .insert([{
              nome_tabela: nomeTabela,
              tipo_cobranca: 'Kwp',
              valor_unitario: 0,
              id_empresa: idEmpresa
            }])
            .select('id')
            .single()
          if (insertTabelaError) throw insertTabelaError
          tabelaPrecoId = inserted?.id
          if (!tabelaPrecoId) throw new Error('Não foi possível obter o ID da tabela de preço.')
        }
        for (const f of faixasValidas) {
          const kwpMin = f.kwp_min === '' ? 0 : parseFloat(String(f.kwp_min).replace(',', '.')) || 0
          const kwpMax = f.kwp_max === '' ? 0 : parseFloat(String(f.kwp_max).replace(',', '.')) || 0
          const valor = f.valor === '' ? 0 : parseFloat(String(f.valor).replace(',', '.')) || 0
          const { error: insertFaixaError } = await supabase
            .from('faixas_kwp')
            .insert([{ tabela_preco_id: tabelaPrecoId, kwp_min: kwpMin, kwp_max: kwpMax, valor }])
          if (insertFaixaError) throw insertFaixaError
        }
      }

      closeModalTabelaPrecos()
      setShowConfirmacaoTabelaPrecos(true)
    } catch (err) {
      console.error('Erro ao cadastrar tabela de preços:', err)
      showErroValidacao(err.message || 'Erro ao cadastrar.')
    } finally {
      setSavingTabelaPrecos(false)
    }
  }

  const totalPlacas = servicos.reduce((s, r) => s + (Number(r.quantidade_modulos) || 0), 0)
  const totalClientes = servicos.length

  const selectedTabelaPreco = selectedTabelaPrecoId ? tiposCobrancaList.find((t) => t.id === parseInt(selectedTabelaPrecoId, 10)) : null
  const showPModuloKwp = selectedTabelaPreco?.tipo_cobranca === 'Kwp'

  const kwpRow = (row) => {
    const qtd = Number(row.quantidade_modulos) || 0
    const pModulo = row.id_cliente_cadastro && kitSolarPorClienteCadastro[row.id_cliente_cadastro] != null ? Number(kitSolarPorClienteCadastro[row.id_cliente_cadastro]) : null
    if (pModulo == null) return '–'
    const kwp = (qtd * pModulo) / 1000
    return kwp.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const pModuloRow = (row) => {
    if (!row.id_cliente_cadastro || kitSolarPorClienteCadastro[row.id_cliente_cadastro] == null) return '–'
    return Number(kitSolarPorClienteCadastro[row.id_cliente_cadastro]).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const getValorVistoriaRow = (row) => {
    if (!isVistoria(row)) return 0
    const vistoriaTipo = tiposServicoFiltroList.find((t) => (t.nome || '').trim().toLowerCase() === 'vistoria')
    const idTipoRow = row.tipo_servico_id == null ? '' : String(row.tipo_servico_id).trim()
    const idVistoriaRaw = vistoriaTipo?.id != null ? String(vistoriaTipo.id).trim() : idTipoRow
    const idVistoria = idVistoriaRaw && idVistoriaRaw.length > 10 ? idVistoriaRaw.toLowerCase() : idVistoriaRaw
    if (selectedTabelaPreco && valorServicoPorTipoTabela && Object.keys(valorServicoPorTipoTabela).length > 0) {
      const valorTabela =
        valorServicoPorTipoTabela[idVistoria] ??
        valorServicoPorTipoTabela[idTipoRow] ??
        (idTipoRow && idTipoRow.length > 10 ? valorServicoPorTipoTabela[idTipoRow.toLowerCase()] : undefined) ??
        valorServicoPorTipoTabela._vistoria
      if (valorTabela != null) return Number(valorTabela) || 0
    }
    if (row.valor_servico_vistoria != null) return Number(row.valor_servico_vistoria) || 0
    return vistoriaTipo != null ? Number(vistoriaTipo.valor_servico) || 0 : 0
  }

  const getValorPadraoRow = (row) => {
    if (isServicoSemValores(row)) return 0
    const idPadrao = row.tipo_padrao_id == null ? '' : String(row.tipo_padrao_id)
    return Number(valorPadraoPorTipoPadrao[idPadrao]) || 0
  }

  const getValorServicoTipoRow = (row) => {
    if (isInstalacaoNome(row.tipo_servico?.nome)) return null
    // Obra Cívil é cobrada pelo Valor da Obra; ignora o valor da tabela de preço.
    if (isObraCivilNome(row.tipo_servico?.nome)) return null
    const idTipo = row.tipo_servico_id == null ? '' : String(row.tipo_servico_id)
    if (!idTipo) return null
    const valor = valorServicoPorTipoTabela[idTipo]
    return valor != null ? Number(valor) || 0 : null
  }

  const isDesinstalacaoReinstalacao = (row) => tipoPorQuantidadeModulos(row.tipo_servico?.nome)

  const valorMaterialRow = (row) => {
    // A lista de material é vinculada à Ordem de Serviço (qualquer tipo). Para linhas que têm
    // id_ordem_servico usamos o total daquela OS; para as demais (vindas de `clientes`, sem OS)
    // usamos o total do cliente como fallback.
    if (row.id_ordem_servico != null) return totalFornecidoPorOrdem[row.id_ordem_servico] || 0
    if (row.id_cliente_cadastro != null) return totalMaterialPorCliente[row.id_cliente_cadastro] || 0
    return 0
  }

  // A linha possui lista de material vinculada (por OS ou, como fallback, pelo cliente).
  const temListaMaterial = (row) => {
    if (row.id_ordem_servico != null) return ordensComListaMaterial.has(row.id_ordem_servico)
    return row.id_cliente_cadastro != null && clientesComListaMaterial.has(row.id_cliente_cadastro)
  }

  const totalRow = (row) => {
    if (isOrdemServico(row) && !temDadosInstalacao(row) && getValorServicoTipoRow(row) == null) {
      const vm = valorMaterialRow(row)
      return vm > 0 ? formatarMoeda(vm) : '–'
    }
    if (isVistoria(row)) {
      const valorVistoria = getValorVistoriaRow(row)
      const deslocamento = Number(row.deslocamento) || 0
      return formatarMoeda(valorVistoria + deslocamento)
    }
    const valorObra = Number(row.valor_obra) || 0
    const valorMaterial = valorMaterialRow(row)
    const deslocamento = Number(row.deslocamento) || 0
    const somaObraMaterial = valorObra + valorMaterial + deslocamento
    // Obra Cívil: usa apenas o Valor da Obra (+ deslocamento), ignorando a tabela de preço.
    if (isObraCivilNome(row.tipo_servico?.nome)) return formatarMoeda(somaObraMaterial)
    if (!selectedTabelaPreco) return formatarMoeda(somaObraMaterial)
    const tipo = selectedTabelaPreco.tipo_cobranca
    const valorPadrao = getValorPadraoRow(row)
    const valorServicoTipo = getValorServicoTipoRow(row)
    if (tipo === 'Modulo') {
      let baseTotal
      if (valorServicoTipo != null) {
        if (isDesinstalacaoReinstalacao(row)) {
          const qtd = Number(row.quantidade_modulos) || 0
          baseTotal = qtd * valorServicoTipo
        } else {
          baseTotal = valorServicoTipo
        }
      } else {
        const qtd = Number(row.quantidade_modulos) || 0
        const valorUnitario = Number(selectedTabelaPreco.valor_unitario) || 0
        baseTotal = qtd * valorUnitario
      }
      return formatarMoeda(baseTotal + valorPadrao + valorObra + valorMaterial + deslocamento)
    }
    if (tipo === 'Kwp') {
      let baseTotal
      if (valorServicoTipo != null) {
        baseTotal = valorServicoTipo
      } else {
        const qtd = Number(row.quantidade_modulos) || 0
        const pModulo = row.id_cliente_cadastro && kitSolarPorClienteCadastro[row.id_cliente_cadastro] != null ? Number(kitSolarPorClienteCadastro[row.id_cliente_cadastro]) : null
        if (pModulo == null) return formatarMoeda(somaObraMaterial)
        const kwp = (qtd * pModulo) / 1000
        const faixa = faixasKwpSelecionada.find((f) => {
          const min = Number(f.kwp_min) || 0
          const max = Number(f.kwp_max) || 0
          return kwp >= min && kwp <= max
        })
        if (!faixa) return formatarMoeda(somaObraMaterial)
        const valorFaixa = Number(faixa.valor) || 0
        baseTotal = valorFaixa * kwp
      }
      return formatarMoeda(baseTotal + valorPadrao + valorObra + valorMaterial + deslocamento)
    }
    return formatarMoeda(somaObraMaterial)
  }

  const totalRowAviso = (row) => {
    if (isServicoSemValores(row)) return null
    if (!selectedTabelaPreco) return 'Selecione uma tabela de preço'
    const tipo = selectedTabelaPreco.tipo_cobranca
    if (tipo === 'Kwp') {
      const qtd = Number(row.quantidade_modulos) || 0
      const pModulo = row.id_cliente_cadastro && kitSolarPorClienteCadastro[row.id_cliente_cadastro] != null ? Number(kitSolarPorClienteCadastro[row.id_cliente_cadastro]) : null
      if (pModulo == null) return 'P.módulo não informado'
      const kwp = (qtd * pModulo) / 1000
      const faixa = faixasKwpSelecionada.find((f) => {
        const min = Number(f.kwp_min) || 0
        const max = Number(f.kwp_max) || 0
        return kwp >= min && kwp <= max
      })
      if (!faixa) return 'Faixa não encontrada'
    }
    return null
  }

  const totalRowNumeric = (row) => {
    if (isOrdemServico(row) && !temDadosInstalacao(row) && getValorServicoTipoRow(row) == null) return valorMaterialRow(row)
    if (isVistoria(row)) {
      const valorVistoria = getValorVistoriaRow(row)
      const deslocamento = Number(row.deslocamento) || 0
      return valorVistoria + deslocamento
    }
    const valorObra = Number(row.valor_obra) || 0
    const valorMaterial = valorMaterialRow(row)
    const deslocamento = Number(row.deslocamento) || 0
    const somaObraMaterial = valorObra + valorMaterial + deslocamento
    // Obra Cívil: usa apenas o Valor da Obra (+ deslocamento), ignorando a tabela de preço.
    if (isObraCivilNome(row.tipo_servico?.nome)) return somaObraMaterial
    if (!selectedTabelaPreco) return somaObraMaterial
    const tipo = selectedTabelaPreco.tipo_cobranca
    const valorPadrao = getValorPadraoRow(row)
    const valorServicoTipo = getValorServicoTipoRow(row)
    if (tipo === 'Modulo') {
      let baseTotal
      if (valorServicoTipo != null) {
        if (isDesinstalacaoReinstalacao(row)) {
          const qtd = Number(row.quantidade_modulos) || 0
          baseTotal = qtd * valorServicoTipo
        } else {
          baseTotal = valorServicoTipo
        }
      } else {
        const qtd = Number(row.quantidade_modulos) || 0
        const valorUnitario = Number(selectedTabelaPreco.valor_unitario) || 0
        baseTotal = qtd * valorUnitario
      }
      return baseTotal + valorPadrao + valorObra + valorMaterial + deslocamento
    }
    if (tipo === 'Kwp') {
      let baseTotal
      if (valorServicoTipo != null) {
        baseTotal = valorServicoTipo
      } else {
        const qtd = Number(row.quantidade_modulos) || 0
        const pModulo = row.id_cliente_cadastro && kitSolarPorClienteCadastro[row.id_cliente_cadastro] != null ? Number(kitSolarPorClienteCadastro[row.id_cliente_cadastro]) : null
        if (pModulo == null) return somaObraMaterial
        const kwp = (qtd * pModulo) / 1000
        const faixa = faixasKwpSelecionada.find((f) => {
          const min = Number(f.kwp_min) || 0
          const max = Number(f.kwp_max) || 0
          return kwp >= min && kwp <= max
        })
        if (!faixa) return somaObraMaterial
        const valorFaixa = Number(faixa.valor) || 0
        baseTotal = valorFaixa * kwp
      }
      return baseTotal + valorPadrao + valorObra + valorMaterial + deslocamento
    }
    return somaObraMaterial
  }

  const montarReplicaMedicao = (row, loteId, alvo) => {
    const qtd = numeroOuNull(row.quantidade_modulos)
    const pModulo = row.id_cliente_cadastro && kitSolarPorClienteCadastro[row.id_cliente_cadastro] != null
      ? Number(kitSolarPorClienteCadastro[row.id_cliente_cadastro])
      : null
    const kwp = qtd != null && pModulo != null ? (qtd * pModulo) / 1000 : null
    const contratante = row.id_contratante
      ? empresasContratadoras.find((c) => Number(c.id) === Number(row.id_contratante))
      : null
    const valorPadrao = isVistoria(row) || isServicoSemValores(row) ? null : getValorPadraoRow(row)
    const valorServicoTipo = getValorServicoTipoRow(row)
    const valorVistoria = isVistoria(row) ? getValorVistoriaRow(row) : null
    return {
      lote_id: loteId,
      periodo_inicio: filtroDataInicio || null,
      periodo_fim: filtroDataFim || null,
      origem: alvo.tabela === 'vistorias' ? 'vistorias' : (row.origem || 'clientes'),
      id_cliente: alvo.tabela === 'clientes' ? alvo.id : null,
      id_vistoria: alvo.tabela === 'vistorias' ? alvo.id : (row.id_vistoria ?? null),
      id_cliente_cadastro: row.id_cliente_cadastro || null,
      id_ordem_servico: row.id_ordem_servico != null ? String(row.id_ordem_servico) : null,
      nome_cliente: row.nome_cliente || null,
      data_servico: dataServicoISO(row),
      tipo_servico: row.tipo_servico?.nome || null,
      tipo_servico_id: row.tipo_servico_id != null ? String(row.tipo_servico_id) : null,
      tipo_padrao: row.tipo_padrao?.nome || null,
      tipo_padrao_id: row.tipo_padrao_id != null ? String(row.tipo_padrao_id) : null,
      quantidade_modulos: qtd,
      total_modulos: getTotalModulosRow(row),
      potencia_modulo: pModulo,
      kwp,
      valor_padrao: valorPadrao,
      valor_obra: numeroOuNull(row.valor_obra),
      valor_material: temListaMaterial(row) ? valorMaterialRow(row) : null,
      deslocamento: numeroOuNull(row.deslocamento),
      valor_servico: valorVistoria != null ? valorVistoria : valorServicoTipo,
      valor_total: totalRowNumeric(row),
      tipo_cobranca: selectedTabelaPreco?.tipo_cobranca || null,
      nome_tabela_preco: selectedTabelaPreco?.nome_tabela || null,
      id_tabela_preco: selectedTabelaPreco?.id ?? null,
      id_contratante: row.id_contratante != null ? Number(row.id_contratante) : null,
      nome_contratante: contratante?.nome_empresa || contratante?.razao_social || null,
      empresa_id: row.empresa_id || userEmpresa?.id || null,
      km_rodado: numeroOuNull(row.km_rodado),
      tipo_veiculo: row.tipo_veiculo || null,
      id_usuario: userId || null
    }
  }

  const somaTotalColuna = servicos.reduce((acc, row) => acc + (totalRowNumeric(row) ?? 0), 0)

  const tipoCobrancaLabel = selectedTabelaPreco?.tipo_cobranca === 'Kwp' ? 'Kwp' : (selectedTabelaPreco?.tipo_cobranca === 'Modulo' ? 'Módulo' : '–')

  const exportarServicosExcel = () => {
    if (servicos.length === 0) {
      showErroValidacao('Não há serviços para exportar.')
      return
    }
    const rows = servicos.map((row) => ({
      'Nome do cliente': row.nome_cliente || '–',
      'Data instalação': formatarData(row.data_instalacao),
      'Tipo de serviço': row.tipo_servico?.nome ? `${row.tipo_servico.nome}${row.origem === 'ordem_servico' ? ' (ordem)' : ''}` : '–',
      'Tipo padrão': isServicoSemValores(row) ? '–' : (row.tipo_padrao?.nome ?? '–'),
      'Tipo de cobrança': tipoCobrancaLabel,
      'Qtd. módulo': isServicoSemValores(row) ? null : (Number(row.quantidade_modulos) || 0),
      'Total Módulos': isServicoSemValores(row) ? null : (getTotalModulosRow(row) ?? null),
      'Valor padrão': isServicoSemValores(row) || !selectedTabelaPreco ? null : getValorPadraoRow(row),
      'Valor obra': isServicoSemValores(row) ? null : (Number(row.valor_obra) || 0),
      'Valor material': temListaMaterial(row) ? valorMaterialRow(row) : null,
      Total: totalRowNumeric(row)
    }))

    const ws = XLSX.utils.json_to_sheet(rows)
    ws['!cols'] = [
      { wch: 30 }, { wch: 14 }, { wch: 28 }, { wch: 20 }, { wch: 16 },
      { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 12 }
    ]
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Serviços')
    XLSX.writeFile(wb, `servicos_${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  const gerarReciboPdf = () => {
    if (!selectedTabelaPreco) {
      showErroValidacao('Selecione um Tipo de cobrança para gerar o recibo.')
      return
    }
    if (servicos.length === 0) {
      showErroValidacao('Não há serviços para gerar o recibo.')
      return
    }
    const doc = new jsPDF('p', 'mm', 'a4')
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 18
    const lineHeight = 7
    const tableLeft = margin
    const tableWidth = pageWidth - margin * 2
    const colW = [40, 28, 24, 24, 28]
    const rowH = 8
    const headers = ['Nome do cliente', 'Tipo de serviço', 'Valor obra', 'Valor material', 'Total']

    let y = margin
    doc.setFontSize(16)
    doc.setFont('helvetica', 'bold')
    doc.text('RECIBO', pageWidth / 2, y, { align: 'center' })
    y += lineHeight * 1.5

    const maxTextWidth = tableWidth
    const drawTextWrap = (text, x, y0) => {
      const lines = doc.splitTextToSize(text, maxTextWidth)
      lines.forEach((line) => { doc.text(line, x, y0); y0 += lineHeight })
      return y0
    }
    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    doc.text('Dados da empresa', margin, y)
    y += lineHeight
    doc.setFont('helvetica', 'normal')
    const nomeEmpresa = userEmpresa?.empresa ?? userEmpresa?.nome ?? '–'
    const razaoSocialEmpresa = userEmpresa?.razao_social ?? '–'
    const cnpjEmpresa = userEmpresa?.cnpj ?? '–'
    const enderecoEmpresa = userEmpresa?.endereco ?? '–'
    y = drawTextWrap(`Empresa: ${nomeEmpresa}`, margin, y)
    y = drawTextWrap(`Razão Social: ${razaoSocialEmpresa}`, margin, y)
    y = drawTextWrap(`CNPJ/CPF: ${cnpjEmpresa}`, margin, y)
    y = drawTextWrap(`Endereço: ${enderecoEmpresa}`, margin, y)
    y += lineHeight * 0.5

    const idContratante = servicos[0]?.id_contratante
    let contratante = idContratante ? empresasContratadoras.find((c) => c.id === idContratante) : null
    if (!contratante && filtroEmpresa) {
      const empresaSelecionadaRecibo = listaEmpresas.find((e) => e.id === filtroEmpresa)
      if (empresaSelecionadaRecibo) {
        contratante = {
          nome_empresa: empresaSelecionadaRecibo.empresa ?? '–',
          razao_social: empresaSelecionadaRecibo.razao_social ?? '–',
          cnpj_cpf: empresaSelecionadaRecibo.cnpj ?? '–',
          endereco: empresaSelecionadaRecibo.endereco ?? '–',
          numero: null
        }
      }
    }
    if (contratante) {
      doc.setFont('helvetica', 'bold')
      doc.text('Dados da contratante', margin, y)
      y += lineHeight
      doc.setFont('helvetica', 'normal')
      const nomeContratante = contratante.nome_empresa ?? '–'
      const razaoContratante = contratante.razao_social ?? '–'
      const cnpjContratante = contratante.cnpj_cpf ?? '–'
      const enderecoContratante = [contratante.endereco, contratante.numero].filter(Boolean).join(', N° ') || '–'
      y = drawTextWrap(`Empresa: ${nomeContratante}`, margin, y)
      y = drawTextWrap(`Razão Social: ${razaoContratante}`, margin, y)
      y = drawTextWrap(`CNPJ/CPF: ${cnpjContratante}`, margin, y)
      y = drawTextWrap(`Endereço: ${enderecoContratante}`, margin, y)
      y += lineHeight * 0.5
    }

    const bancoEmpresaRecibo = userEmpresa?.banco ?? ''
    const agenciaEmpresaRecibo = userEmpresa?.agencia ?? ''
    const contaEmpresaRecibo = userEmpresa?.conta ?? ''
    const chavePixEmpresaRecibo = userEmpresa?.chave_pix ?? ''
    if (bancoEmpresaRecibo || agenciaEmpresaRecibo || contaEmpresaRecibo || chavePixEmpresaRecibo) {
      doc.setFont('helvetica', 'bold')
      doc.text('Dados bancários (Empresa)', margin, y)
      y += lineHeight
      doc.setFont('helvetica', 'normal')
      if (bancoEmpresaRecibo) y = drawTextWrap(`Banco: ${bancoEmpresaRecibo}`, margin, y)
      if (agenciaEmpresaRecibo) y = drawTextWrap(`Agência: ${agenciaEmpresaRecibo}`, margin, y)
      if (contaEmpresaRecibo) y = drawTextWrap(`Conta: ${contaEmpresaRecibo}`, margin, y)
      if (chavePixEmpresaRecibo) y = drawTextWrap(`Chave PIX: ${chavePixEmpresaRecibo}`, margin, y)
      y += lineHeight * 0.5
    }

    doc.setFont('helvetica', 'bold')
    doc.text('Dados do serviço', margin, y)
    y += lineHeight
    doc.setFont('helvetica', 'normal')
    doc.text(`Tipo de Cobrança: ${tipoCobrancaLabel || '–'}`, margin, y)
    y += lineHeight

    const drawTableHeader = () => {
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(9)
      let x = tableLeft
      headers.forEach((h, i) => {
        doc.text(h, x + 2, y + rowH / 2 - 1)
        x += colW[i]
      })
      doc.setDrawColor(0, 0, 0)
      doc.setLineWidth(0.3)
      doc.rect(tableLeft, y - 4, tableWidth, rowH)
      x = tableLeft
      colW.forEach((w, i) => {
        if (i > 0) doc.line(x, y - 4, x, y - 4 + rowH)
        x += w
      })
      y += rowH
    }

    const drawTableRow = (values) => {
      if (y + rowH > pageHeight - margin) {
        doc.addPage()
        y = margin
        drawTableHeader()
      }
      doc.setFont('helvetica', 'normal')
      let x = tableLeft
      values.forEach((v, i) => {
        const str = String(v)
        const lines = doc.splitTextToSize(str, colW[i] - 4)
        doc.text(lines[0] || '–', x + 2, y + rowH / 2 - 1)
        x += colW[i]
      })
      doc.rect(tableLeft, y - 4, tableWidth, rowH)
      x = tableLeft
      colW.forEach((w, i) => {
        if (i > 0) doc.line(x, y - 4, x, y - 4 + rowH)
        x += w
      })
      y += rowH
    }

    drawTableHeader()
    servicos.forEach((row) => {
      drawTableRow([
        row.nome_cliente || '–',
        row.tipo_servico?.nome ?? '–',
        formatarMoeda(row.valor_obra),
        formatarMoeda(valorMaterialRow(row)),
        totalRow(row)
      ])
    })
    doc.setFont('helvetica', 'bold')
    drawTableRow(['TOTAL', '', '', '', formatarMoeda(somaTotalColuna)])

    const fileName = `Recibo_Servicos_${new Date().toISOString().slice(0, 10)}.pdf`
    doc.save(fileName)
  }

  /** Gera PDF no mesmo formato da tela Medição de Clientes (um recibo por cliente, tabela SERVIÇO/QTD/VALOR/TOTAL por linha) */
  const gerarRecibosPdfMedicao = () => {
    if (!selectedTabelaPreco) {
      showErroValidacao('Selecione um Tipo de cobrança para gerar o PDF.')
      return
    }
    if (servicos.length === 0) {
      showErroValidacao('Não há serviços para gerar recibos.')
      return
    }
    try {
      const pdf = new jsPDF('p', 'mm', 'a4')
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const MARGIN = 20
      const CONTENT_WIDTH = pageWidth - 2 * MARGIN
      const COLUMN_WIDTHS = { SERVICO: 70, QTD: 25, PRECO: 35, TOTAL: 40 }
      const COLUMNS = {
        SERVICO: MARGIN + 5,
        QTD: MARGIN + COLUMN_WIDTHS.SERVICO + 10,
        PRECO: MARGIN + COLUMN_WIDTHS.SERVICO + COLUMN_WIDTHS.QTD + 15,
        TOTAL: MARGIN + COLUMN_WIDTHS.SERVICO + COLUMN_WIDTHS.QTD + COLUMN_WIDTHS.PRECO + 20
      }
      const HEIGHTS = { TITLE: 15, SUBTITLE: 10, HEADER: 8, ROW: 6, SEPARATOR: 5, MARGIN_SMALL: 3, MARGIN_MEDIUM: 8, MARGIN_LARGE: 15 }
      const COLORS = { HEADER_BG: [255, 165, 0], HEADER_TEXT: [255, 255, 255], SEPARATOR: [200, 200, 200], SEPARATOR_STRONG: [150, 150, 150], TOTAL_LINE: [0, 0, 0] }

      const formatCurrency = (value) => Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      const setHeaderStyle = () => { pdf.setFont('helvetica', 'bold'); pdf.setTextColor(...COLORS.HEADER_TEXT); pdf.setFontSize(10) }
      const setNormalStyle = () => { pdf.setFont('helvetica', 'normal'); pdf.setTextColor(0, 0, 0); pdf.setFontSize(9) }
      const setBoldStyle = () => { pdf.setFont('helvetica', 'bold'); pdf.setTextColor(0, 0, 0); pdf.setFontSize(10) }
      const setTitleStyle = () => { pdf.setFont('helvetica', 'bold'); pdf.setTextColor(0, 0, 0); pdf.setFontSize(24) }
      const setSubtitleStyle = () => { pdf.setFont('helvetica', 'bold'); pdf.setTextColor(0, 0, 0); pdf.setFontSize(12) }
      const addNewPageIfNeeded = (requiredSpace) => {
        if (currentY + requiredSpace > pageHeight - MARGIN) { pdf.addPage(); currentY = MARGIN; return true }
        return false
      }

      let currentY = MARGIN
      let totalGeral = 0

      setTitleStyle()
      pdf.text('RECIBO', pageWidth / 2, currentY, { align: 'center' })
      currentY += HEIGHTS.TITLE

      const maxTextWidthEmpresa = CONTENT_WIDTH / 2 - 5
      const maxTextWidthContratante = CONTENT_WIDTH / 2 - 5
      const drawTextWrapPdf = (text, x, y0, maxW) => {
        const lines = pdf.splitTextToSize(text, maxW)
        lines.forEach((line) => { pdf.text(line, x, y0); y0 += HEIGHTS.ROW })
        return y0
      }
      setSubtitleStyle()
      pdf.text('Empresa', MARGIN, currentY)
      currentY += HEIGHTS.SUBTITLE
      setNormalStyle()
      pdf.setFontSize(10)
      const nomeEmpresaPdf = userEmpresa?.empresa ?? userEmpresa?.nome ?? '–'
      const razaoSocialEmpresaPdf = userEmpresa?.razao_social ?? '–'
      const cnpjEmpresaPdf = userEmpresa?.cnpj ?? '–'
      const enderecoEmpresaPdf = userEmpresa?.endereco ?? '–'
      currentY = drawTextWrapPdf('Empresa: ' + nomeEmpresaPdf, MARGIN, currentY, maxTextWidthEmpresa)
      currentY = drawTextWrapPdf('Razão Social: ' + razaoSocialEmpresaPdf, MARGIN, currentY, maxTextWidthEmpresa)
      currentY = drawTextWrapPdf('CNPJ/CPF: ' + cnpjEmpresaPdf, MARGIN, currentY, maxTextWidthEmpresa)
      currentY = drawTextWrapPdf('Endereço: ' + enderecoEmpresaPdf, MARGIN, currentY, maxTextWidthEmpresa)
      currentY += HEIGHTS.MARGIN_MEDIUM

      const idContratantePdf = servicos[0]?.id_contratante
      let contratantePdf = idContratantePdf ? empresasContratadoras.find((c) => c.id === idContratantePdf) : null
      if (!contratantePdf && filtroEmpresa) {
        const empresaSelecionada = listaEmpresas.find((e) => e.id === filtroEmpresa)
        if (empresaSelecionada) {
          contratantePdf = {
            nome_empresa: empresaSelecionada.empresa ?? '–',
            razao_social: empresaSelecionada.razao_social ?? '–',
            cnpj_cpf: empresaSelecionada.cnpj ?? '–',
            endereco: empresaSelecionada.endereco ?? '–',
            numero: null
          }
        }
      }
      let contratanteY = MARGIN + HEIGHTS.TITLE + HEIGHTS.SUBTITLE
      setSubtitleStyle()
      pdf.text('Contratante', MARGIN + CONTENT_WIDTH / 2, MARGIN + HEIGHTS.TITLE)
      setNormalStyle()
      pdf.setFontSize(10)
      const contratanteX = MARGIN + CONTENT_WIDTH / 2
      if (contratantePdf) {
        const nomeCt = contratantePdf.nome_empresa ?? '–'
        const razaoCt = contratantePdf.razao_social ?? '–'
        const cnpjCt = contratantePdf.cnpj_cpf ?? '–'
        const enderecoCt = [contratantePdf.endereco, contratantePdf.numero].filter(Boolean).join(', N° ') || '–'
        contratanteY = drawTextWrapPdf('Empresa: ' + nomeCt, contratanteX, contratanteY, maxTextWidthContratante)
        contratanteY = drawTextWrapPdf('Razão Social: ' + razaoCt, contratanteX, contratanteY, maxTextWidthContratante)
        contratanteY = drawTextWrapPdf('CNPJ/CPF: ' + cnpjCt, contratanteX, contratanteY, maxTextWidthContratante)
        contratanteY = drawTextWrapPdf('Endereço: ' + enderecoCt, contratanteX, contratanteY, maxTextWidthContratante)
      } else {
        pdf.text('Empresa: –', contratanteX, contratanteY)
        contratanteY += HEIGHTS.ROW
        pdf.text('Razão Social: –', contratanteX, contratanteY)
        contratanteY += HEIGHTS.ROW
        pdf.text('CNPJ/CPF: –', contratanteX, contratanteY)
        contratanteY += HEIGHTS.ROW
        pdf.text('Endereço: –', contratanteX, contratanteY)
        contratanteY += HEIGHTS.ROW
      }

      currentY = Math.max(currentY, contratanteY) + HEIGHTS.MARGIN_MEDIUM
      const bancoEmpresa = userEmpresa?.banco ?? ''
      const agenciaEmpresa = userEmpresa?.agencia ?? ''
      const contaEmpresa = userEmpresa?.conta ?? ''
      const chavePixEmpresa = userEmpresa?.chave_pix ?? ''
      if (bancoEmpresa || agenciaEmpresa || contaEmpresa || chavePixEmpresa) {
        setSubtitleStyle()
        pdf.text('Dados bancários (Empresa)', MARGIN, currentY)
        currentY += HEIGHTS.SUBTITLE
        setNormalStyle()
        pdf.setFontSize(10)
        if (bancoEmpresa) currentY = drawTextWrapPdf('Banco: ' + bancoEmpresa, MARGIN, currentY, CONTENT_WIDTH)
        if (agenciaEmpresa) currentY = drawTextWrapPdf('Agência: ' + agenciaEmpresa, MARGIN, currentY, CONTENT_WIDTH)
        if (contaEmpresa) currentY = drawTextWrapPdf('Conta: ' + contaEmpresa, MARGIN, currentY, CONTENT_WIDTH)
        if (chavePixEmpresa) currentY = drawTextWrapPdf('Chave PIX: ' + chavePixEmpresa, MARGIN, currentY, CONTENT_WIDTH)
        currentY += HEIGHTS.MARGIN_MEDIUM
      }

      setSubtitleStyle()
      pdf.text('DESCRIÇÃO DE ATIVIDADES: INSTALAÇÃO DE SISTEMA FOTOVOLTAICO CONECTADO A REDE', pageWidth / 2, currentY, { align: 'center' })
      currentY += HEIGHTS.MARGIN_LARGE

      servicos.forEach((row, index) => {
        addNewPageIfNeeded(50)
        setBoldStyle()
        const nomeCliente = row.nome_cliente || 'Cliente sem nome'
        pdf.text('CLIENTE: ' + nomeCliente, MARGIN, currentY)
        currentY += HEIGHTS.ROW
        setNormalStyle()
        const dataCliente = formatarDataRecibo(row)
        pdf.text('DATA: ' + dataCliente, MARGIN, currentY)
        currentY += HEIGHTS.MARGIN_MEDIUM

        pdf.setFillColor(...COLORS.HEADER_BG)
        pdf.rect(MARGIN, currentY, CONTENT_WIDTH, HEIGHTS.HEADER, 'F')
        setHeaderStyle()
        pdf.text('SERVIÇO', COLUMNS.SERVICO, currentY + HEIGHTS.HEADER - 2)
        pdf.text('QTD', COLUMNS.QTD, currentY + HEIGHTS.HEADER - 2)
        pdf.text('VALOR', COLUMNS.PRECO, currentY + HEIGHTS.HEADER - 2)
        pdf.text('TOTAL', COLUMNS.TOTAL, currentY + HEIGHTS.HEADER - 2)
        currentY += HEIGHTS.HEADER + HEIGHTS.MARGIN_SMALL

        setNormalStyle()
        const deslocamentoValor = Number(row.deslocamento) || 0
        const totalCliente = totalRowNumeric(row) ?? 0
        const servicoNome = row.tipo_servico?.nome || 'Instalação'

        if (isOrdemServico(row) && !temDadosInstalacao(row) && getValorServicoTipoRow(row) == null) {
          pdf.text(servicoNome, COLUMNS.SERVICO, currentY)
          pdf.text('–', COLUMNS.QTD, currentY)
          pdf.text('–', COLUMNS.PRECO, currentY)
          pdf.text('–', COLUMNS.TOTAL, currentY)
          currentY += HEIGHTS.ROW
        } else if (isVistoria(row)) {
          const valorVistoria = getValorVistoriaRow(row)
          pdf.text(servicoNome, COLUMNS.SERVICO, currentY)
          pdf.text('1', COLUMNS.QTD, currentY)
          pdf.text(formatCurrency(valorVistoria), COLUMNS.PRECO, currentY)
          pdf.text(formatCurrency(valorVistoria), COLUMNS.TOTAL, currentY)
          currentY += HEIGHTS.ROW
          if (deslocamentoValor > 0) {
            pdf.text('Deslocamento', COLUMNS.SERVICO, currentY)
            pdf.text('1', COLUMNS.QTD, currentY)
            pdf.text(formatCurrency(deslocamentoValor), COLUMNS.PRECO, currentY)
            pdf.text(formatCurrency(deslocamentoValor), COLUMNS.TOTAL, currentY)
            currentY += HEIGHTS.ROW
          }
        } else {
          const valorObra = Number(row.valor_obra) || 0
          const valorMaterial = valorMaterialRow(row)
          const valorPadrao = getValorPadraoRow(row)
          const padraoNome = row.tipo_padrao?.nome || ''
          const isInstalacao = servicoNome === 'Instalação'
          const isObra = isObraCivilNome(servicoNome)
          const qtd = isInstalacao ? (Number(row.quantidade_modulos) || 0) : (Number(row.quantidade_modulos) || 1)
          const tipo = selectedTabelaPreco.tipo_cobranca
          const valorServicoTipo = getValorServicoTipoRow(row)

          let valorUnitarioInstalacao = 0
          let totalInstalacao = 0
          if (isInstalacao) {
            valorUnitarioInstalacao = Number(selectedTabelaPreco.valor_unitario) || 0
            totalInstalacao = qtd * valorUnitarioInstalacao
          } else if (isObra) {
            // Obra Cívil: cobrança pelo Valor da Obra; sem linha de serviço da tabela.
          } else if (valorServicoTipo != null) {
            valorUnitarioInstalacao = valorServicoTipo
            totalInstalacao = tipoPorQuantidadeModulos(servicoNome)
              ? (Number(row.quantidade_modulos) || 0) * valorServicoTipo
              : valorServicoTipo
          } else if (tipo === 'Modulo') {
            valorUnitarioInstalacao = Number(selectedTabelaPreco.valor_unitario) || 0
            totalInstalacao = qtd * valorUnitarioInstalacao
          } else if (tipo === 'Kwp') {
            const pModulo = row.id_cliente_cadastro && kitSolarPorClienteCadastro[row.id_cliente_cadastro] != null ? Number(kitSolarPorClienteCadastro[row.id_cliente_cadastro]) : null
            if (pModulo != null) {
              const kwp = (qtd * pModulo) / 1000
              const faixa = faixasKwpSelecionada.find((f) => {
                const min = Number(f.kwp_min) || 0
                const max = Number(f.kwp_max) || 0
                return kwp >= min && kwp <= max
              })
              if (faixa) {
                const valorFaixa = Number(faixa.valor) || 0
                valorUnitarioInstalacao = valorFaixa
                totalInstalacao = valorFaixa * kwp
              }
            }
          }

          if (!isObra) {
            const qtdExibir = (!isInstalacao && valorServicoTipo != null)
              ? (tipoPorQuantidadeModulos(servicoNome) ? (Number(row.quantidade_modulos) || 0) : 1)
              : qtd
            pdf.text(servicoNome, COLUMNS.SERVICO, currentY)
            pdf.text(String(qtdExibir), COLUMNS.QTD, currentY)
            pdf.text(formatCurrency(valorUnitarioInstalacao), COLUMNS.PRECO, currentY)
            pdf.text(formatCurrency(totalInstalacao), COLUMNS.TOTAL, currentY)
            currentY += HEIGHTS.ROW
          }

          if (valorPadrao > 0) {
            pdf.text(padraoNome || 'Padrão', COLUMNS.SERVICO, currentY)
            pdf.text('1', COLUMNS.QTD, currentY)
            pdf.text(formatCurrency(valorPadrao), COLUMNS.PRECO, currentY)
            pdf.text(formatCurrency(valorPadrao), COLUMNS.TOTAL, currentY)
            currentY += HEIGHTS.ROW
          }

          if (valorMaterial > 0) {
            pdf.text('Valor material', COLUMNS.SERVICO, currentY)
            pdf.text('1', COLUMNS.QTD, currentY)
            pdf.text(formatCurrency(valorMaterial), COLUMNS.PRECO, currentY)
            pdf.text(formatCurrency(valorMaterial), COLUMNS.TOTAL, currentY)
            currentY += HEIGHTS.ROW
          }

          if (valorObra > 0) {
            pdf.text('Valor obra', COLUMNS.SERVICO, currentY)
            pdf.text('1', COLUMNS.QTD, currentY)
            pdf.text(formatCurrency(valorObra), COLUMNS.PRECO, currentY)
            pdf.text(formatCurrency(valorObra), COLUMNS.TOTAL, currentY)
            currentY += HEIGHTS.ROW
          }

          if (deslocamentoValor > 0) {
            pdf.text('Deslocamento', COLUMNS.SERVICO, currentY)
            pdf.text('1', COLUMNS.QTD, currentY)
            pdf.text(formatCurrency(deslocamentoValor), COLUMNS.PRECO, currentY)
            pdf.text(formatCurrency(deslocamentoValor), COLUMNS.TOTAL, currentY)
            currentY += HEIGHTS.ROW
          }
        }

        pdf.setDrawColor(...COLORS.SEPARATOR)
        pdf.line(MARGIN, currentY, pageWidth - MARGIN, currentY)
        currentY += HEIGHTS.SEPARATOR
        totalGeral += totalCliente
        setBoldStyle()
        pdf.text('TOTAL FINAL: R$ ' + formatCurrency(totalCliente), MARGIN, currentY)
        currentY += HEIGHTS.MARGIN_LARGE

        if (index < servicos.length - 1) {
          pdf.setDrawColor(...COLORS.SEPARATOR_STRONG)
          pdf.line(MARGIN, currentY, pageWidth - MARGIN, currentY)
          currentY += HEIGHTS.MARGIN_MEDIUM
        }
      })

      addNewPageIfNeeded(40)
      pdf.setDrawColor(...COLORS.TOTAL_LINE)
      pdf.setLineWidth(2)
      pdf.line(MARGIN, currentY, pageWidth - MARGIN, currentY)
      currentY += HEIGHTS.MARGIN_LARGE
      setBoldStyle()
      pdf.setFontSize(16)
      pdf.text('TOTAL GERAL DE TODOS OS RECIBOS: R$ ' + formatCurrency(totalGeral), pageWidth / 2, currentY, { align: 'center' })
      currentY += HEIGHTS.SUBTITLE
      setNormalStyle()
      pdf.setFontSize(10)
      pdf.text(`Total de clientes: ${servicos.length}`, pageWidth / 2, currentY, { align: 'center' })
      currentY += HEIGHTS.ROW
      pdf.text(`Data de geração: ${new Date().toLocaleDateString('pt-BR')}`, pageWidth / 2, currentY, { align: 'center' })

      pdf.save(`recibos_servicos_${new Date().toISOString().split('T')[0]}.pdf`)
      setShowConfirmacaoPadraoCobranca(true)
      setMsgConfirmacaoPadrao(`Recibos gerados com sucesso! ${servicos.length} cliente(s) incluído(s).`)
    } catch (err) {
      console.error('Erro ao gerar PDF:', err)
      showErroValidacao('Erro ao gerar PDF: ' + (err.message || 'Erro desconhecido'))
    }
  }

  /** Gera recibo em DOC (Word) no mesmo formato do PDF Medição */
  const gerarRecibosDocMedicao = async () => {
    if (!selectedTabelaPreco) {
      showErroValidacao('Selecione um Tipo de cobrança para gerar o recibo.')
      return
    }
    if (servicos.length === 0) {
      showErroValidacao('Não há serviços para gerar recibos.')
      return
    }
    const formatCurrency = (value) => Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    const p = (text, bold = false, align = null) => new Paragraph({
      children: [new TextRun({ text, bold })],
      alignment: align === 'center' ? AlignmentType.CENTER : undefined,
      spacing: { after: 120 }
    })
    const pEmpty = () => new Paragraph({ spacing: { after: 120 } })
    const pSection = (text) => new Paragraph({
      children: [new TextRun({ text, bold: true })],
      spacing: { before: 240, after: 120 }
    })

    const nomeEmpresaDoc = userEmpresa?.empresa ?? userEmpresa?.nome ?? '–'
    const razaoSocialEmpresaDoc = userEmpresa?.razao_social ?? '–'
    const cnpjEmpresaDoc = userEmpresa?.cnpj ?? '–'
    const enderecoEmpresaDoc = userEmpresa?.endereco ?? '–'
    const idContratanteDoc = servicos[0]?.id_contratante
    let contratanteDoc = idContratanteDoc ? empresasContratadoras.find((c) => c.id === idContratanteDoc) : null
    if (!contratanteDoc && filtroEmpresa) {
      const empresaSelecionadaDoc = listaEmpresas.find((e) => e.id === filtroEmpresa)
      if (empresaSelecionadaDoc) {
        contratanteDoc = {
          nome_empresa: empresaSelecionadaDoc.empresa ?? '–',
          razao_social: empresaSelecionadaDoc.razao_social ?? '–',
          cnpj_cpf: empresaSelecionadaDoc.cnpj ?? '–',
          endereco: empresaSelecionadaDoc.endereco ?? '–',
          numero: null
        }
      }
    }
    const bancoDoc = userEmpresa?.banco ?? ''
    const agenciaDoc = userEmpresa?.agencia ?? ''
    const contaDoc = userEmpresa?.conta ?? ''
    const chavePixDoc = userEmpresa?.chave_pix ?? ''

    const children = [
      p('RECIBO', true, 'center'),
      pEmpty(),
      pSection('Empresa'),
      p('Empresa: ' + nomeEmpresaDoc),
      p('Razão Social: ' + razaoSocialEmpresaDoc),
      p('CNPJ/CPF: ' + cnpjEmpresaDoc),
      p('Endereço: ' + enderecoEmpresaDoc),
      pEmpty(),
      pSection('Contratante'),
      ...(contratanteDoc
        ? [
            p('Empresa: ' + (contratanteDoc.nome_empresa ?? '–')),
            p('Razão Social: ' + (contratanteDoc.razao_social ?? '–')),
            p('CNPJ/CPF: ' + (contratanteDoc.cnpj_cpf ?? '–')),
            p('Endereço: ' + [contratanteDoc.endereco, contratanteDoc.numero].filter(Boolean).join(', N° ') || '–')
          ]
        : [p('Empresa: –'), p('Razão Social: –'), p('CNPJ/CPF: –'), p('Endereço: –')]),
      pEmpty()
    ]
    if (bancoDoc || agenciaDoc || contaDoc || chavePixDoc) {
      children.push(pSection('Dados bancários (Empresa)'))
      if (bancoDoc) children.push(p('Banco: ' + bancoDoc))
      if (agenciaDoc) children.push(p('Agência: ' + agenciaDoc))
      if (contaDoc) children.push(p('Conta: ' + contaDoc))
      if (chavePixDoc) children.push(p('Chave PIX: ' + chavePixDoc))
      children.push(pEmpty())
    }
    children.push(p('DESCRIÇÃO DE ATIVIDADES: INSTALAÇÃO DE SISTEMA FOTOVOLTAICO CONECTADO A REDE', false, 'center'))
    children.push(pEmpty())

    let totalGeralDoc = 0
    servicos.forEach((row, index) => {
      const nomeCliente = row.nome_cliente || 'Cliente sem nome'
      const dataCliente = formatarDataRecibo(row)
      const deslocamentoValor = Number(row.deslocamento) || 0
      const totalCliente = totalRowNumeric(row) ?? 0
      const servicoNome = row.tipo_servico?.nome || 'Instalação'

      children.push(pSection('CLIENTE: ' + nomeCliente))
      children.push(p('DATA: ' + dataCliente))
      children.push(p('SERVIÇO\tQTD\tVALOR\tTOTAL', true))

      if (isOrdemServico(row) && !temDadosInstalacao(row) && getValorServicoTipoRow(row) == null) {
        children.push(p(servicoNome + '\t–\t–\t–'))
      } else if (isVistoria(row)) {
        const valorVistoria = getValorVistoriaRow(row)
        children.push(p(servicoNome + '\t1\tR$ ' + formatCurrency(valorVistoria) + '\tR$ ' + formatCurrency(valorVistoria)))
        if (deslocamentoValor > 0) children.push(p('Deslocamento\t1\tR$ ' + formatCurrency(deslocamentoValor) + '\tR$ ' + formatCurrency(deslocamentoValor)))
      } else {
        const valorObra = Number(row.valor_obra) || 0
        const valorMaterial = valorMaterialRow(row)
        const valorPadrao = getValorPadraoRow(row)
        const padraoNome = row.tipo_padrao?.nome || ''
        const isInstalacao = servicoNome === 'Instalação'
        const isObra = isObraCivilNome(servicoNome)
        const qtd = isInstalacao ? (Number(row.quantidade_modulos) || 0) : (Number(row.quantidade_modulos) || 1)
        const tipo = selectedTabelaPreco.tipo_cobranca
        const valorServicoTipo = getValorServicoTipoRow(row)
        let valorUnitarioInstalacao = 0
        let totalInstalacao = 0
        if (isInstalacao) {
          valorUnitarioInstalacao = Number(selectedTabelaPreco.valor_unitario) || 0
          totalInstalacao = qtd * valorUnitarioInstalacao
        } else if (isObra) {
          // Obra Cívil: cobrança pelo Valor da Obra; sem linha de serviço da tabela.
        } else if (valorServicoTipo != null) {
          valorUnitarioInstalacao = valorServicoTipo
          totalInstalacao = tipoPorQuantidadeModulos(servicoNome)
            ? (Number(row.quantidade_modulos) || 0) * valorServicoTipo
            : valorServicoTipo
        } else if (tipo === 'Modulo') {
          valorUnitarioInstalacao = Number(selectedTabelaPreco.valor_unitario) || 0
          totalInstalacao = qtd * valorUnitarioInstalacao
        } else if (tipo === 'Kwp') {
          const pModulo = row.id_cliente_cadastro && kitSolarPorClienteCadastro[row.id_cliente_cadastro] != null ? Number(kitSolarPorClienteCadastro[row.id_cliente_cadastro]) : null
          if (pModulo != null) {
            const kwp = (qtd * pModulo) / 1000
            const faixa = faixasKwpSelecionada.find((f) => {
              const min = Number(f.kwp_min) || 0
              const max = Number(f.kwp_max) || 0
              return kwp >= min && kwp <= max
            })
            if (faixa) {
              const valorFaixa = Number(faixa.valor) || 0
              valorUnitarioInstalacao = valorFaixa
              totalInstalacao = valorFaixa * kwp
            }
          }
        }
        if (!isObra) {
          const qtdExibir = (!isInstalacao && valorServicoTipo != null)
            ? (tipoPorQuantidadeModulos(servicoNome) ? (Number(row.quantidade_modulos) || 0) : 1)
            : qtd
          children.push(p(servicoNome + '\t' + qtdExibir + '\tR$ ' + formatCurrency(valorUnitarioInstalacao) + '\tR$ ' + formatCurrency(totalInstalacao)))
        }
        if (valorPadrao > 0) children.push(p((padraoNome || 'Padrão') + '\t1\tR$ ' + formatCurrency(valorPadrao) + '\tR$ ' + formatCurrency(valorPadrao)))
        if (valorMaterial > 0) children.push(p('Valor material\t1\tR$ ' + formatCurrency(valorMaterial) + '\tR$ ' + formatCurrency(valorMaterial)))
        if (valorObra > 0) children.push(p('Valor obra\t1\tR$ ' + formatCurrency(valorObra) + '\tR$ ' + formatCurrency(valorObra)))
        if (deslocamentoValor > 0) children.push(p('Deslocamento\t1\tR$ ' + formatCurrency(deslocamentoValor) + '\tR$ ' + formatCurrency(deslocamentoValor)))
      }
      children.push(p('TOTAL FINAL: R$ ' + formatCurrency(totalCliente), true))
      children.push(pEmpty())
      if (index < servicos.length - 1) children.push(p('—'.repeat(40)))
      children.push(pEmpty())
      totalGeralDoc += totalCliente
    })

    children.push(p('TOTAL GERAL DE TODOS OS RECIBOS: R$ ' + formatCurrency(totalGeralDoc), true, 'center'))
    children.push(p('Total de clientes: ' + servicos.length, false, 'center'))
    children.push(p('Data de geração: ' + new Date().toLocaleDateString('pt-BR'), false, 'center'))

    try {
      const doc = new Document({ sections: [{ properties: {}, children }] })
      const blob = await Packer.toBlob(doc)
      saveAs(blob, `recibos_servicos_${new Date().toISOString().split('T')[0]}.docx`)
      setShowModalGerarRecibo(false)
      setShowConfirmacaoPadraoCobranca(true)
      setMsgConfirmacaoPadrao(`Recibos gerados com sucesso! ${servicos.length} cliente(s) incluído(s).`)
    } catch (err) {
      console.error('Erro ao gerar DOC:', err)
      showErroValidacao('Erro ao gerar documento: ' + (err.message || 'Erro desconhecido'))
    }
  }

  const handleGerarReciboPdf = () => {
    setShowModalGerarRecibo(false)
    gerarRecibosPdfMedicao()
  }

  const handleGerarReciboDoc = () => {
    setShowModalGerarRecibo(false)
    gerarRecibosDocMedicao()
  }

  return (
    <div className="servicos-container">
      <div className="servicos-header">
        <h2>Serviços</h2>
        <button type="button" className="servicos-btn-back" onClick={onBack}>
          ← Voltar
        </button>
      </div>

      <div className="servicos-cards">
        <div className="servicos-card">
          <span className="servicos-card-label">Total de placas</span>
          <span className="servicos-card-value">{loading ? '–' : totalPlacas.toLocaleString('pt-BR')}</span>
        </div>
        <div className="servicos-card">
          <span className="servicos-card-label">Total de clientes</span>
          <span className="servicos-card-value">{loading ? '–' : totalClientes.toLocaleString('pt-BR')}</span>
        </div>
        <div className="servicos-card">
          <span className="servicos-card-label">Valor Total</span>
          <span className="servicos-card-value">{loading ? '–' : formatarMoeda(somaTotalColuna)}</span>
        </div>
      </div>

      <div className="servicos-filtro">
        {userRole === 'administrador' && (
          <label className="servicos-filtro-label">
            Empresa
            <select
              className="servicos-filtro-input servicos-filtro-select"
              value={filtroEmpresa}
              onChange={(e) => setFiltroEmpresa(e.target.value)}
              aria-label="Filtrar por empresa (somente administrador)"
            >
              <option value="">Todas</option>
              {listaEmpresas.map((emp) => (
                <option key={emp.id} value={emp.id}>{emp.empresa ?? '–'}</option>
              ))}
            </select>
          </label>
        )}
        <label className="servicos-filtro-label">
          Data início
          <input
            type="date"
            className="servicos-filtro-input"
            value={filtroDataInicio}
            onChange={(e) => setFiltroDataInicio(e.target.value)}
          />
        </label>
        <label className="servicos-filtro-label">
          Data final
          <input
            type="date"
            className="servicos-filtro-input"
            value={filtroDataFim}
            onChange={(e) => setFiltroDataFim(e.target.value)}
          />
        </label>
        <label className="servicos-filtro-label">
          Tipo de serviço
          <select
            className="servicos-filtro-input servicos-filtro-select"
            value={filtroTipoServico}
            onChange={(e) => setFiltroTipoServico(e.target.value)}
            aria-label="Filtrar por tipo de serviço"
          >
            <option value="">Todos</option>
            {tiposServicoFiltroList.map((t) => (
              <option key={t.id} value={t.id}>{t.nome ?? '–'}</option>
            ))}
          </select>
        </label>
        <label className="servicos-filtro-label">
          Tipo de cobrança
          <select
            className="servicos-filtro-input servicos-filtro-select"
            value={selectedTabelaPrecoId}
            onChange={(e) => setSelectedTabelaPrecoId(e.target.value)}
            aria-label="Tabela de preço: tipo de cobrança e nome"
          >
            <option value="">–</option>
            {tiposCobrancaList.map((t) => (
              <option key={t.id} value={t.id}>
                {t.tipo_cobranca} – {t.nome_tabela || '–'}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="servicos-btn-filtrar" onClick={loadServicos}>
          Filtrar
        </button>
        <button type="button" className="servicos-btn-recibo" onClick={exportarServicosExcel} title="Exportar para Excel">
          Exportar Excel
        </button>
        {userRole === 'administrador' && (
          <button
            type="button"
            className="servicos-btn-fechar-medicao"
            onClick={() => abrirConfirmacaoFecharMedicao()}
            disabled={loading || servicos.length === 0}
            title="Fechar medição dos serviços listados e bloquear edição da baixa"
          >
            Fechar Medição
          </button>
        )}
        {selectedTabelaPrecoId && (
          <>
            <button type="button" className="servicos-btn-recibo" onClick={gerarReciboPdf} style={{ display: 'none' }}>
              Gerar recibo (PDF)
            </button>
            <button type="button" className="servicos-btn-recibo" onClick={() => setShowModalGerarRecibo(true)} title="Gerar recibo">
              Gerar Recibo
            </button>
          </>
        )}
        <div className="servicos-cadastros-wrap" ref={cadastrosWrapRef}>
          <button
            type="button"
            className="servicos-btn-cadastros"
            onClick={() => setShowCadastrosMenu(prev => !prev)}
            aria-expanded={showCadastrosMenu}
            aria-haspopup="true"
          >
            Cadastros <span className="servicos-cadastros-chevron">{showCadastrosMenu ? '▲' : '▼'}</span>
          </button>
          {showCadastrosMenu && (
            <div className="servicos-cadastros-dropdown" role="menu">
              <button
                type="button"
                className="servicos-cadastros-item"
                role="menuitem"
                onClick={() => { setShowCadastrosMenu(false); openModalContratadora() }}
              >
                Cadastrar Empresa Contratadora
              </button>
              <button
                type="button"
                className="servicos-cadastros-item"
                role="menuitem"
                onClick={() => { setShowCadastrosMenu(false); openModalPadraoCobranca() }}
              >
                Tipo Padrão Cobrança
              </button>
              <button
                type="button"
                className="servicos-cadastros-item"
                role="menuitem"
                onClick={() => { setShowCadastrosMenu(false); openModalTabelaPrecos() }}
              >
                Cadastrar Tabela de Preço
              </button>
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="servicos-loading">Carregando...</div>
      )}

      {error && (
        <div className="servicos-error alert alert-danger">
          {error}
        </div>
      )}

      {showModalContratadora && (
        <div className="servicos-modal-overlay" onClick={closeModalContratadora}>
          <div className="servicos-modal" onClick={(e) => e.stopPropagation()}>
            <div className="servicos-modal-header">
              <h3>Cadastrar Empresa Contratadora</h3>
              <button type="button" className="servicos-modal-close" onClick={closeModalContratadora} aria-label="Fechar">×</button>
            </div>
            <form className="servicos-modal-form" onSubmit={handleSubmitContratadora}>
              <div className="servicos-modal-body">
                {msgContratadora.text && (
                  <div className={`servicos-modal-msg ${msgContratadora.type === 'error' ? 'error' : 'success'}`} role="alert">
                    {msgContratadora.text}
                  </div>
                )}
                <section className="servicos-form-section">
                  <h4 className="servicos-form-section-title">Tipo de cadastro</h4>
                  <div className="servicos-tipo-cadastro">
                    <label className={`servicos-tipo-option ${tipoCadastroContratadora === 'cnpj' ? 'active' : ''}`}>
                      <input type="radio" name="tipo-cadastro" value="cnpj" checked={tipoCadastroContratadora === 'cnpj'} onChange={() => handleTipoCadastroChange('cnpj')} />
                      <span>CNPJ</span>
                    </label>
                    <label className={`servicos-tipo-option ${tipoCadastroContratadora === 'cpf' ? 'active' : ''}`}>
                      <input type="radio" name="tipo-cadastro" value="cpf" checked={tipoCadastroContratadora === 'cpf'} onChange={() => handleTipoCadastroChange('cpf')} />
                      <span>CPF</span>
                    </label>
                  </div>
                </section>
                <section className="servicos-form-section">
                  <h4 className="servicos-form-section-title">{tipoCadastroContratadora === 'cpf' ? 'Dados da pessoa' : 'Dados da empresa'}</h4>
                  <div className="servicos-form-row">
                    <div className={`servicos-form-group ${tipoCadastroContratadora === 'cnpj' ? '' : 'servicos-form-group-full'}`}>
                      <label htmlFor="ec-nome_empresa">{tipoCadastroContratadora === 'cpf' ? 'Nome completo' : 'Nome da empresa'}</label>
                      <input id="ec-nome_empresa" type="text" value={formContratadora.nome_empresa} onChange={(e) => setFormContratadora({ ...formContratadora, nome_empresa: e.target.value })} placeholder={tipoCadastroContratadora === 'cpf' ? 'Nome completo' : 'Nome da empresa'} />
                    </div>
                    {tipoCadastroContratadora === 'cnpj' && (
                      <div className="servicos-form-group">
                        <label htmlFor="ec-razao_social">Razão social</label>
                        <input id="ec-razao_social" type="text" value={formContratadora.razao_social} onChange={(e) => setFormContratadora({ ...formContratadora, razao_social: e.target.value })} placeholder="Razão social" />
                      </div>
                    )}
                  </div>
                  <div className="servicos-form-row">
                    <div className="servicos-form-group">
                      <label htmlFor="ec-cnpj_cpf">{tipoCadastroContratadora === 'cpf' ? 'CPF' : 'CNPJ ou CPF'}</label>
                      <input id="ec-cnpj_cpf" type="text" value={formContratadora.cnpj_cpf} onChange={(e) => setFormContratadora({ ...formContratadora, cnpj_cpf: e.target.value })} placeholder={tipoCadastroContratadora === 'cpf' ? 'CPF' : 'CNPJ ou CPF'} />
                    </div>
                  </div>
                </section>
                <section className="servicos-form-section">
                  <h4 className="servicos-form-section-title">Contato</h4>
                  <div className="servicos-form-row">
                    <div className="servicos-form-group">
                      <label htmlFor="ec-telefone">Telefone</label>
                      <input id="ec-telefone" type="text" value={formContratadora.telefone} onChange={(e) => setFormContratadora({ ...formContratadora, telefone: e.target.value })} placeholder="(00) 00000-0000" />
                    </div>
                    <div className="servicos-form-group">
                      <label htmlFor="ec-email">E-mail</label>
                      <input id="ec-email" type="email" value={formContratadora.email} onChange={(e) => setFormContratadora({ ...formContratadora, email: e.target.value })} placeholder="e-mail@exemplo.com" />
                    </div>
                  </div>
                </section>
                <section className="servicos-form-section">
                  <h4 className="servicos-form-section-title">Endereço</h4>
                  <div className="servicos-form-row">
                    <div className="servicos-form-group servicos-form-group-cep">
                      <label htmlFor="ec-cep">CEP</label>
                      <div className="servicos-cep-wrap">
                        <input id="ec-cep" type="text" inputMode="numeric" value={cepDisplay(formContratadora.cep)} onChange={handleCepChange} onBlur={handleCepBlur} placeholder="00000-000" maxLength={9} />
                        <button type="button" className="servicos-btn-buscar-cep" onClick={buscarCepContratadora} disabled={searchCepContratadora} title="Buscar endereço">
                          {searchCepContratadora ? <span className="servicos-cep-spinner" /> : 'Buscar'}
                        </button>
                      </div>
                      <span className="servicos-cep-hint">Preencha o CEP e clique em Buscar ou saia do campo para carregar o endereço.</span>
                    </div>
                  </div>
                  <div className="servicos-form-row">
                    <div className="servicos-form-group">
                      <label htmlFor="ec-endereco">Endereço</label>
                      <input id="ec-endereco" type="text" value={formContratadora.endereco} onChange={(e) => setFormContratadora({ ...formContratadora, endereco: e.target.value })} placeholder="Logradouro" />
                    </div>
                    <div className="servicos-form-group servicos-form-group-numero">
                      <label htmlFor="ec-numero">Número</label>
                      <input id="ec-numero" type="text" value={formContratadora.numero} onChange={(e) => setFormContratadora({ ...formContratadora, numero: e.target.value })} placeholder="Nº" />
                    </div>
                  </div>
                  <div className="servicos-form-row">
                    <div className="servicos-form-group">
                      <label htmlFor="ec-bairro">Bairro</label>
                      <input id="ec-bairro" type="text" value={formContratadora.bairro} onChange={(e) => setFormContratadora({ ...formContratadora, bairro: e.target.value })} placeholder="Bairro" />
                    </div>
                    <div className="servicos-form-group">
                      <label htmlFor="ec-cidade">Cidade</label>
                      <input id="ec-cidade" type="text" value={formContratadora.cidade} onChange={(e) => setFormContratadora({ ...formContratadora, cidade: e.target.value })} placeholder="Cidade" />
                    </div>
                  </div>
                  <div className="servicos-form-row">
                    <div className="servicos-form-group servicos-form-group-uf">
                      <label htmlFor="ec-estado">Estado (UF)</label>
                      <input id="ec-estado" type="text" value={formContratadora.estado} onChange={(e) => setFormContratadora({ ...formContratadora, estado: e.target.value.toUpperCase().slice(0, 2) })} placeholder="UF" maxLength={2} />
                    </div>
                  </div>
                </section>
              </div>
              <div className="servicos-modal-footer">
                <button type="button" className="servicos-btn-cancel" onClick={closeModalContratadora} disabled={savingContratadora}>Cancelar</button>
                <button type="submit" className="servicos-btn-save" disabled={savingContratadora}>{savingContratadora ? 'Salvando…' : 'Cadastrar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showConfirmacaoCadastro && (
        <div className="servicos-confirm-overlay" onClick={() => setShowConfirmacaoCadastro(false)}>
          <div className="servicos-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="servicos-confirm-icon">✓</div>
            <h3 className="servicos-confirm-title">Cadastro realizado</h3>
            <p className="servicos-confirm-msg">Empresa contratadora cadastrada com sucesso.</p>
            <button type="button" className="servicos-confirm-btn" onClick={() => setShowConfirmacaoCadastro(false)}>
              OK
            </button>
          </div>
        </div>
      )}

      {showModalPadraoCobranca && (
        <div className="servicos-modal-overlay" onClick={closeModalPadraoCobranca}>
          <div className="servicos-modal" onClick={(e) => e.stopPropagation()}>
            <div className="servicos-modal-header">
              <h3>Cadastrar Tipo Padrão Cobrança</h3>
              <button type="button" className="servicos-modal-close" onClick={closeModalPadraoCobranca} aria-label="Fechar">×</button>
            </div>
            <form className="servicos-modal-form" onSubmit={handleSubmitPadraoCobranca}>
              <div className="servicos-modal-body">
                <section className="servicos-form-section">
                  <div className="servicos-form-row">
                    <div className="servicos-form-group servicos-form-group-full">
                      <label htmlFor="pc-tabela_preco">Tabela de preço</label>
                      <select
                        id="pc-tabela_preco"
                        value={formPadraoCobranca.tabela_preco_id}
                        onChange={(e) => loadPadroesCobrancaTabela(e.target.value)}
                      >
                        <option value="">Selecione</option>
                        {tabelaPrecosList.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.nome_tabela || `Tabela ${t.id}`} – {t.tipo_cobranca ?? '–'}
                          </option>
                        ))}
                      </select>
                      {tabelaPrecosList.length === 0 && (
                        <span className="servicos-form-hint">Cadastre primeiro uma tabela de preço.</span>
                      )}
                    </div>
                  </div>
                  {formPadraoCobranca.tabela_preco_id && (
                    <>
                      <h4 className="servicos-form-section-title">Tipos de padrão cadastrados para esta tabela</h4>
                      <div className="servicos-padroes-cobranca-table-wrap">
                        <table className="servicos-faixas-kwp-table servicos-padroes-cobranca-table">
                          <thead>
                            <tr>
                              <th>Tipo de padrão</th>
                              <th>Valor (R$)</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {listaPadroesCobrancaTabela.length === 0 ? (
                              <tr>
                                <td colSpan={3} className="servicos-faixas-empty">Nenhum tipo de padrão cadastrado para esta tabela.</td>
                              </tr>
                            ) : (
                              listaPadroesCobrancaTabela.map((r) => (
                                <tr key={r.id}>
                                  <td>
                                    <select
                                      className="servicos-select-padrao-edit"
                                      value={idPadraoEditPadrao[r.id] ?? r.id_padrao ?? ''}
                                      onChange={(e) => setIdPadraoEditPadrao(prev => ({ ...prev, [r.id]: e.target.value }))}
                                    >
                                      {tipoPadraoList.map((p) => (
                                        <option key={p.id} value={p.id}>
                                          {p.nome ?? '–'}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      inputMode="decimal"
                                      step="0.01"
                                      min="0"
                                      className="servicos-input-valor-padrao"
                                      value={valorEditPadrao[r.id] ?? (r.valor != null ? r.valor : '')}
                                      onChange={(e) => setValorEditPadrao(prev => ({ ...prev, [r.id]: e.target.value }))}
                                    />
                                  </td>
                                  <td>
                                    <div className="servicos-padrao-acoes">
                                      <button
                                        type="button"
                                        className="servicos-btn-remove-faixa servicos-btn-atualizar"
                                        onClick={() => handleAtualizarPadrao(r.id)}
                                        disabled={atualizandoValorPadrao === r.id}
                                        title="Atualizar"
                                      >
                                        {atualizandoValorPadrao === r.id ? 'Salvando…' : 'Atualizar'}
                                      </button>
                                      <button
                                        type="button"
                                        className="servicos-btn-remove-faixa servicos-btn-excluir-padrao"
                                        onClick={() => handleExcluirPadrao(r.id)}
                                        disabled={excluindoPadrao === r.id}
                                        title="Excluir"
                                      >
                                        {excluindoPadrao === r.id ? 'Excluindo…' : 'Excluir'}
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                      <h4 className="servicos-form-section-title">Cadastrar novo tipo de padrão</h4>
                    </>
                  )}
                  <div className="servicos-form-row">
                    <div className="servicos-form-group servicos-form-group-full">
                      <label htmlFor="pc-id_padrao">Tipo de padrão</label>
                      <select
                        id="pc-id_padrao"
                        value={formPadraoCobranca.id_padrao}
                        onChange={(e) => setFormPadraoCobranca({ ...formPadraoCobranca, id_padrao: e.target.value })}
                      >
                        <option value="">Selecione</option>
                        {tipoPadraoList.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.nome ?? '–'}
                          </option>
                        ))}
                      </select>
                      {tipoPadraoList.length === 0 && (
                        <span className="servicos-form-hint">Nenhum tipo de padrão cadastrado.</span>
                      )}
                    </div>
                  </div>
                  <div className="servicos-form-row">
                    <div className="servicos-form-group servicos-form-group-full">
                      <label htmlFor="pc-valor">Valor (R$)</label>
                      <input
                        id="pc-valor"
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        value={formPadraoCobranca.valor}
                        onChange={(e) => setFormPadraoCobranca({ ...formPadraoCobranca, valor: e.target.value })}
                        placeholder="0,00"
                      />
                    </div>
                  </div>
                </section>
              </div>
              <div className="servicos-modal-footer">
                <button type="button" className="servicos-btn-cancel" onClick={closeModalPadraoCobranca} disabled={savingPadraoCobranca}>Cancelar</button>
                <button type="submit" className="servicos-btn-save" disabled={savingPadraoCobranca}>{savingPadraoCobranca ? 'Salvando…' : 'Cadastrar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showConfirmacaoPadraoCobranca && (
        <div className="servicos-confirm-overlay" onClick={() => setShowConfirmacaoPadraoCobranca(false)}>
          <div className="servicos-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="servicos-confirm-icon">✓</div>
            <h3 className="servicos-confirm-title">Confirmação</h3>
            <p className="servicos-confirm-msg">{msgConfirmacaoPadrao}</p>
            <button type="button" className="servicos-confirm-btn" onClick={() => setShowConfirmacaoPadraoCobranca(false)}>
              OK
            </button>
          </div>
        </div>
      )}

      {showModalTabelaPrecos && (
        <div className="servicos-modal-overlay" onClick={closeModalTabelaPrecos}>
          <div className="servicos-modal" onClick={(e) => e.stopPropagation()}>
            <div className="servicos-modal-header">
              <h3>Cadastrar Tabela de Preço</h3>
              <button type="button" className="servicos-modal-close" onClick={closeModalTabelaPrecos} aria-label="Fechar">×</button>
            </div>
            <form className="servicos-modal-form" onSubmit={handleSubmitTabelaPrecos}>
              <div className="servicos-modal-body">
                {msgTabelaPrecos.text && (
                  <div className={`servicos-modal-msg ${msgTabelaPrecos.type === 'error' ? 'error' : 'success'}`} role="alert">
                    {msgTabelaPrecos.text}
                  </div>
                )}
                <section className="servicos-form-section">
                  <div className="servicos-form-row">
                    <div className="servicos-form-group servicos-form-group-full">
                      <label>Tipo de cobrança</label>
                      <div className="servicos-tipo-cadastro">
                        <label className={`servicos-tipo-option ${formTabelaPrecos.tipo_cobranca === 'Modulo' ? 'active' : ''}`}>
                          <input
                            type="radio"
                            name="tipo_cobranca_tp"
                            value="Modulo"
                            checked={formTabelaPrecos.tipo_cobranca === 'Modulo'}
                            onChange={() => setFormTabelaPrecos({ ...formTabelaPrecos, tipo_cobranca: 'Modulo', tabela_preco_id_edicao: '' })}
                          />
                          <span>Módulo</span>
                        </label>
                        <label className={`servicos-tipo-option ${formTabelaPrecos.tipo_cobranca === 'Kwp' ? 'active' : ''}`}>
                          <input
                            type="radio"
                            name="tipo_cobranca_tp"
                            value="Kwp"
                            checked={formTabelaPrecos.tipo_cobranca === 'Kwp'}
                            onChange={() => {
                              setFormTabelaPrecos({ ...formTabelaPrecos, tipo_cobranca: 'Kwp', tabela_preco_id_edicao: '' })
                              setFaixasKwp([])
                            }}
                          />
                          <span>Kwp</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="servicos-form-row">
                    <div className="servicos-form-group servicos-form-group-full">
                      <label htmlFor="tp-nome_tabela">Nome da tabela de preço</label>
                      <input
                        id="tp-nome_tabela"
                        type="text"
                        value={formTabelaPrecos.nome_tabela}
                        onChange={(e) => setFormTabelaPrecos({ ...formTabelaPrecos, nome_tabela: e.target.value })}
                        placeholder="Ex.: Tabela padrão 2024"
                      />
                    </div>
                  </div>
                  {formTabelaPrecos.tipo_cobranca === 'Modulo' && (
                    <>
                    <div className="servicos-form-row">
                      <div className="servicos-form-group servicos-form-group-full">
                        <label htmlFor="tp-valor_unitario">Valor unitário (R$)</label>
                        <input
                          id="tp-valor_unitario"
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          min="0"
                          value={formTabelaPrecos.valor_unitario}
                          onChange={(e) => setFormTabelaPrecos({ ...formTabelaPrecos, valor_unitario: e.target.value })}
                          placeholder="0,00"
                        />
                      </div>
                    </div>
                    <div className="servicos-form-row">
                      <div className="servicos-form-group servicos-form-group-full">
                        <label htmlFor="tp-tabela-modulo-edicao">Editar tabela existente (opcional)</label>
                        <select
                          id="tp-tabela-modulo-edicao"
                          value={formTabelaPrecos.tabela_preco_id_edicao}
                          onChange={(e) => handleSelecionarTabelaModuloEdicao(e.target.value)}
                        >
                          <option value="">Nova tabela</option>
                          {tiposCobrancaList.filter(t => t.tipo_cobranca === 'Modulo').map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.nome_tabela || `Tabela ${t.id}`}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {formTabelaPrecos.tabela_preco_id_edicao && tiposServicoList.length > 0 && (
                      <div className="servicos-form-section" style={{ marginTop: '1rem' }}>
                        <h4 className="servicos-form-section-title">Valores por tipo de serviço</h4>
                        <div className="servicos-padroes-cobranca-table-wrap">
                          <table className="servicos-faixas-kwp-table">
                            <thead>
                              <tr>
                                <th>Tipo de Serviço</th>
                                <th>Valor (R$)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tiposServicoList.map((tipo) => (
                                <tr key={tipo.id}>
                                  <td>{tipo.nome || '–'}</td>
                                  <td>
                                    <input
                                      type="number"
                                      inputMode="decimal"
                                      step="0.01"
                                      min="0"
                                      className="servicos-input-valor-padrao"
                                      value={precosServicosPorTipo[tipo.id] ?? ''}
                                      onChange={(e) => setPrecosServicosPorTipo(prev => ({ ...prev, [tipo.id]: e.target.value }))}
                                      placeholder="0,00"
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    </>
                  )}
                  {formTabelaPrecos.tipo_cobranca === 'Kwp' && (
                    <>
                      <div className="servicos-form-row">
                        <div className="servicos-form-group servicos-form-group-full">
                          <label htmlFor="tp-tabela-edicao">Editar tabela existente (opcional)</label>
                          <select
                            id="tp-tabela-edicao"
                            value={formTabelaPrecos.tabela_preco_id_edicao}
                            onChange={(e) => handleSelecionarTabelaKwpEdicao(e.target.value)}
                          >
                            <option value="">Nova tabela</option>
                            {tiposCobrancaList.filter(t => t.tipo_cobranca === 'Kwp').map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.nome_tabela || `Tabela ${t.id}`}
                              </option>
                            ))}
                          </select>
                          {tiposCobrancaList.filter(t => t.tipo_cobranca === 'Kwp').length === 0 && (
                            <span className="servicos-form-hint">Nenhuma tabela Kwp cadastrada. Preencha o nome e adicione as faixas abaixo.</span>
                          )}
                        </div>
                      </div>
                      <h4 className="servicos-form-section-title">Faixas Kwp</h4>
                      <div className="servicos-form-row servicos-form-row-faixa">
                        <div className="servicos-form-group">
                          <label>Kwp mín</label>
                          <input
                            type="number"
                            inputMode="decimal"
                            step="0.0001"
                            min="0"
                            value={currentFaixaKwp.kwp_min}
                            onChange={(e) => setCurrentFaixaKwp(prev => ({ ...prev, kwp_min: e.target.value }))}
                            placeholder="0"
                          />
                        </div>
                        <div className="servicos-form-group">
                          <label>Kwp máx</label>
                          <input
                            type="number"
                            inputMode="decimal"
                            step="0.0001"
                            min="0"
                            value={currentFaixaKwp.kwp_max}
                            onChange={(e) => setCurrentFaixaKwp(prev => ({ ...prev, kwp_max: e.target.value }))}
                            placeholder="0"
                          />
                        </div>
                        <div className="servicos-form-group">
                          <label>Valor (R$)</label>
                          <input
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            min="0"
                            value={currentFaixaKwp.valor}
                            onChange={(e) => setCurrentFaixaKwp(prev => ({ ...prev, valor: e.target.value }))}
                            placeholder="0,00"
                          />
                        </div>
                        <div className="servicos-form-group servicos-form-group-btn-remove">
                          <label>&nbsp;</label>
                          <button type="button" className="servicos-btn-add-faixa" onClick={addFaixaKwp}>
                            Adicionar faixa
                          </button>
                        </div>
                      </div>
                      <div className="servicos-faixas-kwp-table-wrap">
                        <table className="servicos-faixas-kwp-table">
                          <thead>
                            <tr>
                              <th>Kwp mín</th>
                              <th>Kwp máx</th>
                              <th>Valor (R$)</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {faixasKwp.length === 0 ? (
                              <tr>
                                <td colSpan={4} className="servicos-faixas-empty">Nenhuma faixa adicionada. Preencha os campos acima e clique em &quot;Adicionar faixa&quot;.</td>
                              </tr>
                            ) : (
                              faixasKwp.map((faixa, index) => (
                                <tr key={index}>
                                  <td>{faixa.kwp_min}</td>
                                  <td>{faixa.kwp_max}</td>
                                  <td>{faixa.valor === '' ? '0,00' : Number(faixa.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                                  <td>
                                    <button
                                      type="button"
                                      className="servicos-btn-remove-faixa"
                                      onClick={() => removeFaixaKwp(index)}
                                      title="Remover faixa"
                                    >
                                      Remover
                                    </button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </section>
              </div>
              <div className="servicos-modal-footer">
                <button type="button" className="servicos-btn-cancel" onClick={closeModalTabelaPrecos} disabled={savingTabelaPrecos}>Cancelar</button>
                <button type="submit" className="servicos-btn-save" disabled={savingTabelaPrecos}>{savingTabelaPrecos ? 'Salvando…' : 'Cadastrar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showConfirmacaoTabelaPrecos && (
        <div className="servicos-confirm-overlay" onClick={() => setShowConfirmacaoTabelaPrecos(false)}>
          <div className="servicos-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="servicos-confirm-icon">✓</div>
            <h3 className="servicos-confirm-title">Cadastro realizado</h3>
            <p className="servicos-confirm-msg">Tabela de preço cadastrada com sucesso.</p>
            <button type="button" className="servicos-confirm-btn" onClick={() => setShowConfirmacaoTabelaPrecos(false)}>
              OK
            </button>
          </div>
        </div>
      )}

      {showModalValidacao && (
        <div className="servicos-confirm-overlay" onClick={() => setShowModalValidacao(false)}>
          <div className="servicos-modal-validacao servicos-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="servicos-modal-validacao-title">Validação</h3>
            <p className="servicos-confirm-msg">{msgValidacao}</p>
            <button type="button" className="servicos-confirm-btn" onClick={() => setShowModalValidacao(false)}>
              OK
            </button>
          </div>
        </div>
      )}

      {showModalFecharMedicao && (
        <div className="servicos-confirm-overlay" onClick={() => { if (!fechandoMedicao) { setShowModalFecharMedicao(false); setRowFecharMedicao(null) } }}>
          <div className="servicos-confirm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <h3 className="servicos-confirm-title">Fechar Medição</h3>
            <p className="servicos-confirm-msg">
              {rowFecharMedicao
                ? `Fechar a medição de ${rowFecharMedicao.nome_cliente || 'este serviço'}? A baixa não poderá mais ser editada em Gestão Instalação nem em Gerenciar Vistorias.`
                : `Fechar a medição de ${servicos.filter(podeFecharMedicao).length} serviço(s) da lista? As baixas não poderão mais ser editadas em Gestão Instalação nem em Gerenciar Vistorias.`}
            </p>
            <div style={{ display: 'flex', gap: '10px', marginTop: '16px', justifyContent: 'center' }}>
              <button
                type="button"
                className="servicos-btn-cancel"
                disabled={fechandoMedicao}
                onClick={() => { setShowModalFecharMedicao(false); setRowFecharMedicao(null) }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="servicos-btn-fechar-medicao"
                disabled={fechandoMedicao}
                onClick={confirmarFecharMedicao}
              >
                {fechandoMedicao ? 'Fechando…' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {msgSucessoMedicao && (
        <div className="servicos-confirm-overlay" onClick={() => setMsgSucessoMedicao('')}>
          <div className="servicos-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="servicos-confirm-icon">✓</div>
            <h3 className="servicos-confirm-title">Medição fechada</h3>
            <p className="servicos-confirm-msg">{msgSucessoMedicao}</p>
            <button type="button" className="servicos-confirm-btn" onClick={() => setMsgSucessoMedicao('')}>
              OK
            </button>
          </div>
        </div>
      )}

      {showModalGerarRecibo && (
        <div className="servicos-confirm-overlay" onClick={() => setShowModalGerarRecibo(false)}>
          <div className="servicos-confirm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '360px' }}>
            <h3 className="servicos-confirm-title">Gerar Recibo</h3>
            <p className="servicos-confirm-msg">Escolha o formato do arquivo:</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '16px' }}>
              <button type="button" className="servicos-btn-recibo" onClick={handleGerarReciboPdf} style={{ width: '100%' }}>
                Gerar como PDF
              </button>
              <button type="button" className="servicos-btn-recibo" onClick={handleGerarReciboDoc} style={{ width: '100%' }}>
                Gerar como DOC (Word)
              </button>
            </div>
            <button type="button" className="servicos-btn-cancel" onClick={() => setShowModalGerarRecibo(false)} style={{ marginTop: '16px', width: '100%' }}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {showModalDetalheServico && typeof document !== 'undefined' && createPortal(
        <div className="servicos-modal-overlay servicos-detalhe-overlay" onClick={fecharModalDetalheServico}>
          <div className="servicos-modal servicos-detalhe-modal" onClick={(e) => e.stopPropagation()}>
            <div className="servicos-modal-header">
              <h3>
                {detalheServicoTipo === 'fotos'
                  ? (isVistoria(detalheServicoRow)
                    ? 'Vistoria'
                    : (detalheServicoRow?.tipo_servico?.nome || 'Serviço'))
                  : 'Lista de material'}
                {detalheServicoRow?.nome_cliente ? (
                  <span className="servicos-detalhe-subtitle"> — {detalheServicoRow.nome_cliente}</span>
                ) : null}
              </h3>
              <button type="button" className="servicos-modal-close" onClick={fecharModalDetalheServico} aria-label="Fechar">
                ×
              </button>
            </div>
            <div className="servicos-modal-body servicos-detalhe-body">
              {detalheServicoLoading ? (
                <p className="servicos-detalhe-loading">Carregando…</p>
              ) : detalheServicoTipo === 'fotos' ? (
                <>
                  {detalheFotosInfoSecoes.length > 0 && (
                    <div className="servicos-fotos-info-blocos">
                      {detalheFotosInfoSecoes.map((sec, idx) => (
                        <section key={idx} className="servicos-fotos-info-secao">
                          <h4 className="servicos-fotos-info-titulo">{sec.titulo}</h4>
                          <div className="servicos-fotos-info-grid">
                            {sec.linhas.map((ln, j) => (
                              <div key={j} className="servicos-fotos-info-row">
                                <span className="servicos-fotos-info-label">{ln.label}</span>
                                <span className="servicos-fotos-info-val">{ln.value}</span>
                              </div>
                            ))}
                          </div>
                        </section>
                      ))}
                    </div>
                  )}
                  {detalheFotosExtras?.linkLocalizacao && (
                    <p className="servicos-detalhe-link-maps">
                      <strong>Localização:</strong>{' '}
                      <a href={detalheFotosExtras.linkLocalizacao} target="_blank" rel="noopener noreferrer">
                        Abrir no mapa
                      </a>
                    </p>
                  )}
                  <p className="servicos-fotos-secao-fotos-titulo">Fotos</p>
                  {detalheFotosItems.length === 0 ? (
                    <p className="servicos-detalhe-empty">Nenhuma foto encontrada para este registro.</p>
                  ) : (
                    <div className="servicos-fotos-grid">
                      {detalheFotosItems.map((item, idx) => (
                        <figure key={`${item.url}-${idx}`} className="servicos-foto-card">
                          <a href={item.url} target="_blank" rel="noopener noreferrer" className="servicos-foto-link">
                            <img src={item.url} alt={item.label} loading="lazy" className="servicos-foto-thumb" />
                          </a>
                          <figcaption className="servicos-foto-caption">{item.label}</figcaption>
                        </figure>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {detalheListasMaterial.length === 0 ? (
                    <p className="servicos-detalhe-empty">Nenhuma lista de material cadastrada para este cliente.</p>
                  ) : (
                    detalheListasMaterial.map((lista) => (
                      <div key={lista.id} className="servicos-lista-material-bloco">
                        <div className="servicos-lista-material-head">
                          <strong>Lista #{lista.id}</strong>
                          {lista.data_atualizacao && (
                            <span className="servicos-lista-material-meta">
                              Atualizado em {formatarData(lista.data_atualizacao)}
                            </span>
                          )}
                        </div>
                        {lista.observacoes && (
                          <p className="servicos-lista-material-obs">{lista.observacoes}</p>
                        )}
                        {(lista.itens || []).length === 0 ? (
                          <p className="servicos-detalhe-empty servicos-detalhe-empty-inline">Sem itens nesta lista.</p>
                        ) : (
                          <div className="servicos-lista-material-table-wrap">
                            <table className="servicos-lista-material-table">
                              <thead>
                                <tr>
                                  <th>Material</th>
                                  <th>Qtd</th>
                                  <th>VALOR</th>
                                  <th>TOTAL</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(lista.itens || []).map((it) => {
                                  const qtd = Number(it.quantidade)
                                  const vu = it.valor_unitario != null ? Number(it.valor_unitario) : NaN
                                  const totalLinha =
                                    !Number.isNaN(qtd) && !Number.isNaN(vu) ? qtd * vu : null
                                  return (
                                    <tr key={it.id}>
                                      <td>{it.material || '–'}</td>
                                      <td>{it.quantidade != null ? it.quantidade : '–'}</td>
                                      <td>{!Number.isNaN(vu) ? formatarMoeda(vu) : '–'}</td>
                                      <td>{totalLinha != null && !Number.isNaN(totalLinha) ? formatarMoeda(totalLinha) : '–'}</td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {(() => {
                          const itensLista = lista.itens || []
                          const somaItens = itensLista.reduce((s, it) => {
                            const q = Number(it.quantidade)
                            const vu = it.valor_unitario != null ? Number(it.valor_unitario) : NaN
                            if (Number.isNaN(q) || Number.isNaN(vu)) return s
                            return s + q * vu
                          }, 0)
                          const tr = lista.total_resolve != null ? Number(lista.total_resolve) : null
                          const tf = lista.total_fornecido != null ? Number(lista.total_fornecido) : null
                          const totalLista =
                            tr != null || tf != null ? (tr || 0) + (tf || 0) : somaItens
                          return (
                            <div className="servicos-lista-material-totais">
                              <div className="servicos-lista-material-total-geral">
                                <span>TOTAL</span>
                                <strong>{formatarMoeda(totalLista)}</strong>
                              </div>
                            </div>
                          )
                        })()}
                      </div>
                    ))
                  )}
                </>
              )}
            </div>
            <div className="servicos-modal-footer servicos-detalhe-footer">
              <button type="button" className="servicos-btn-cancel" onClick={fecharModalDetalheServico}>
                Fechar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {!loading && !error && (
        isMobile ? (
          <div className="servicos-cards-mobile">
            {servicos.length === 0 ? (
              <p className="servicos-cards-mobile-empty">Nenhum registro encontrado.</p>
            ) : (
              servicos.map((row) => {
                const total = totalRow(row)
                const aviso = totalRowAviso(row)
                return (
                  <div key={row.id} className="servicos-card-mobile">
                    <div className="servicos-card-mobile-row servicos-card-mobile-row-duo">
                      <span><strong>Data instalação</strong> {formatarData(row.data_instalacao)}</span>
                      <span><strong>Qtd. módulo</strong> {isServicoSemValores(row) ? '–' : qtdModulo(row)}</span>
                    </div>
                    <div className="servicos-card-mobile-row">
                      <strong>Total Módulos</strong> {isServicoSemValores(row) ? '–' : (getTotalModulosRow(row) != null ? formatarMoeda(getTotalModulosRow(row)) : '–')}
                    </div>
                    <div className="servicos-card-mobile-row">
                      <strong>Nome do cliente</strong> {row.nome_cliente || '–'}
                    </div>
                    <div className="servicos-card-mobile-row servicos-card-mobile-row-duo">
                      <span><strong>Tipo de serviço</strong> {row.tipo_servico?.nome ? `${row.tipo_servico.nome}${row.origem === 'ordem_servico' ? ' (ordem)' : ''}` : '–'}</span>
                      <span><strong>Tipo padrão</strong> {isServicoSemValores(row) ? '–' : (row.tipo_padrao?.nome ?? '–')}</span>
                    </div>
                    <div className="servicos-card-mobile-row">
                      <strong>Valor Padrão</strong> {isServicoSemValores(row) ? '–' : (selectedTabelaPreco ? formatarMoeda(getValorPadraoRow(row)) : '–')}
                    </div>
                    <div className="servicos-card-mobile-row">
                      <strong>Valor obra</strong> {isServicoSemValores(row) ? '–' : formatarMoeda(row.valor_obra)}
                    </div>
                    <div className="servicos-card-mobile-row">
                      <strong>Valor material</strong> {temListaMaterial(row) ? formatarMoeda(valorMaterialRow(row)) : '–'}
                    </div>
                    <div className={`servicos-card-mobile-row servicos-card-mobile-total ${aviso ? 'servicos-total-aviso' : ''}`} title={aviso || undefined}>
                      <strong>Total</strong> {total}
                    </div>
                    <div className="servicos-card-mobile-acoes">
                      <button
                        type="button"
                        className="servicos-icon-acao-btn"
                        title={isVistoria(row) ? 'Ver fotos da vistoria' : `Ver fotos de ${row.tipo_servico?.nome || 'serviço'}`}
                        onClick={() => abrirModalFotos(row)}
                        disabled={!podeAbrirFotos(row)}
                        aria-label="Ver fotos"
                      >
                        👁️
                      </button>
                      <button
                        type="button"
                        className="servicos-icon-acao-btn"
                        title="Ver lista de material"
                        onClick={() => abrirModalListaMaterial(row)}
                        disabled={!podeAbrirListaMaterial(row)}
                        aria-label="Lista de material"
                      >
                        📋
                      </button>
                      {row.medicao_fechada ? (
                        <span className="servicos-medicao-fechada-badge" title="Medição fechada">🔒</span>
                      ) : userRole === 'administrador' ? (
                        <button
                          type="button"
                          className="servicos-icon-acao-btn"
                          title={podeFecharMedicao(row) ? 'Fechar medição deste serviço' : 'Baixa ainda não cadastrada'}
                          onClick={() => abrirConfirmacaoFecharMedicao(row)}
                          disabled={!podeFecharMedicao(row)}
                          aria-label="Fechar medição"
                        >
                          🔐
                        </button>
                      ) : null}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        ) : (
          <div className="servicos-table-wrapper">
            <table className="servicos-table">
              <thead>
                <tr>
                  <th>Nome do cliente</th>
                  <th>Data instalação</th>
                  <th>Tipo de serviço</th>
                  <th>Tipo padrão</th>
                  <th>Valor Padrão</th>
                  <th>Qtd. módulo</th>
                  <th>Total Módulos</th>
                  {showPModuloKwp && <th>P.módulo</th>}
                  {showPModuloKwp && <th>Kwp</th>}
                  <th>Valor obra</th>
                  <th>Valor material</th>
                  <th>Total</th>
                  <th className="servicos-th-acoes">Detalhes</th>
                </tr>
              </thead>
              <tbody>
                {servicos.length === 0 ? (
                <tr>
                    <td colSpan={showPModuloKwp ? 13 : 11} className="servicos-empty">Nenhum registro encontrado.</td>
                </tr>
                ) : (
                  servicos.map((row) => {
                    const total = totalRow(row)
                    const aviso = totalRowAviso(row)
                    return (
                      <tr key={row.id}>
                        <td>{row.nome_cliente || '–'}</td>
                        <td>{formatarData(row.data_instalacao)}</td>
                        <td>{row.tipo_servico?.nome ? `${row.tipo_servico.nome}${row.origem === 'ordem_servico' ? ' (ordem)' : ''}` : '–'}</td>
                        <td>{isServicoSemValores(row) ? '–' : (row.tipo_padrao?.nome ?? '–')}</td>
                        <td>{isServicoSemValores(row) ? '–' : (selectedTabelaPreco ? formatarMoeda(getValorPadraoRow(row)) : '–')}</td>
                        <td>{isServicoSemValores(row) ? '–' : qtdModulo(row)}</td>
                        <td>{isServicoSemValores(row) ? '–' : (getTotalModulosRow(row) != null ? formatarMoeda(getTotalModulosRow(row)) : '–')}</td>
                        {showPModuloKwp && <td>{isServicoSemValores(row) ? '–' : pModuloRow(row)}</td>}
                        {showPModuloKwp && <td>{isServicoSemValores(row) ? '–' : kwpRow(row)}</td>}
                        <td>{isServicoSemValores(row) ? '–' : formatarMoeda(row.valor_obra)}</td>
                        <td>{temListaMaterial(row) ? formatarMoeda(valorMaterialRow(row)) : '–'}</td>
                        <td className={aviso ? 'servicos-total-aviso' : ''} title={aviso || undefined}>{total}</td>
                        <td className="servicos-td-acoes">
                          <div className="servicos-acoes-cell">
                            <button
                              type="button"
                              className="servicos-icon-acao-btn"
                              title={isVistoria(row) ? 'Ver fotos da vistoria' : `Ver fotos de ${row.tipo_servico?.nome || 'serviço'}`}
                              onClick={() => abrirModalFotos(row)}
                              disabled={!podeAbrirFotos(row)}
                              aria-label="Ver fotos"
                            >
                              👁️
                            </button>
                            <button
                              type="button"
                              className="servicos-icon-acao-btn"
                              title="Ver lista de material"
                              onClick={() => abrirModalListaMaterial(row)}
                              disabled={!podeAbrirListaMaterial(row)}
                              aria-label="Lista de material"
                            >
                              📋
                            </button>
                            {row.medicao_fechada ? (
                              <span className="servicos-medicao-fechada-badge" title="Medição fechada">🔒</span>
                            ) : userRole === 'administrador' ? (
                              <button
                                type="button"
                                className="servicos-icon-acao-btn"
                                title={podeFecharMedicao(row) ? 'Fechar medição deste serviço' : 'Baixa ainda não cadastrada'}
                                onClick={() => abrirConfirmacaoFecharMedicao(row)}
                                disabled={!podeFecharMedicao(row)}
                                aria-label="Fechar medição"
                              >
                                🔐
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  )
}

export default Servicos
